import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { calendarsFor } from '../../utils/businessDate';
import { provisionalSettleDate, resolveSettleDate, settlementCalendars } from '../../utils/settlement';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import BaseTicketScreen from './BaseTicketScreen';
import {
  normalizeTicketStatus,
  TicketWorkflowOverrideHelpers,
  TicketWorkflowOverrides,
} from '../../hooks/useTicketWorkflow';
import { useAuth } from '../../contexts/AuthContext';
import SecurityPicker, { PickedSecurity } from './shared/SecurityPicker';
import { FormRow, FormGroup, Label, Input, Select, FieldError } from './shared/TicketStyledComponents';
import { getTodayDate, isBusinessDay, parseDateString } from '../../utils/dateUtils';
import { formatAmountWithCommas, parseAmountShorthand } from '../../utils/amountUtils';
import axios from 'axios';
import styled from 'styled-components';
import {
  Activity,
  ArrowDownToLine,
  ArrowUpFromLine,
  BadgeDollarSign,
  Banknote,
  Building2,
  CheckCircle2,
  CircleDollarSign,
  FileText,
  Landmark,
  Layers,
  Percent,
  Search,
  ShieldCheck,
  Wallet,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { DateField } from '../shared/DateField';

// ─── Styled components ────────────────────────────────────────────────────────

type ModeTone = 'buy' | 'sell' | 'draw' | 'pay';

const toneColor = (tone: ModeTone) => {
  switch (tone) {
    case 'sell':
      return '#f59e0b';
    case 'draw':
      return '#38bdf8';
    case 'pay':
      return '#34d399';
    default:
      return '#60a5fa';
  }
};

const toneBackground = (tone: ModeTone, active = false) => {
  const alpha = active ? 0.18 : 0.08;
  switch (tone) {
    case 'sell':
      return `rgba(245, 158, 11, ${alpha})`;
    case 'draw':
      return `rgba(56, 189, 248, ${alpha})`;
    case 'pay':
      return `rgba(52, 211, 153, ${alpha})`;
    default:
      return `rgba(96, 165, 250, ${alpha})`;
  }
};

const CreditWorkspace = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const WorkspaceBand = styled.section`
  border: 1px solid rgba(51, 65, 85, 0.74);
  background: linear-gradient(180deg, rgba(15, 23, 42, 0.78) 0%, rgba(8, 13, 23, 0.9) 100%);
  border-radius: 14px;
  padding: 18px;
`;

const LateBookingControls = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const LateBookingControlCard = styled.div<{ $approved: boolean }>`
  border: 1px solid ${({ $approved }) => $approved ? 'rgba(52, 211, 153, 0.5)' : 'rgba(245, 158, 11, 0.5)'};
  background: ${({ $approved }) => $approved ? 'rgba(6, 78, 59, 0.18)' : 'rgba(120, 53, 15, 0.16)'};
  border-radius: 10px;
  padding: 14px;
`;

const LateBookingControlTitle = styled.div`
  color: #e2e8f0;
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 5px;
`;

const LateBookingApprovalButton = styled.button`
  width: 100%;
  margin-top: 10px;
  border: 1px solid rgba(96, 165, 250, 0.55);
  border-radius: 8px;
  padding: 9px 12px;
  background: rgba(37, 99, 235, 0.18);
  color: #bfdbfe;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.48;
  }
`;

const BandHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 18px;
  margin-bottom: 18px;

  @media (max-width: 860px) {
    flex-direction: column;
  }
`;

const BandTitleGroup = styled.div`
  min-width: 0;
`;

const BandEyebrow = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 11px;
  font-weight: 800;
  color: #7dd3fc;
  letter-spacing: 0.11em;
  text-transform: uppercase;
  margin-bottom: 6px;
`;

const BandTitle = styled.h3`
  margin: 0;
  color: #f8fafc;
  font-size: 18px;
  line-height: 1.25;
  letter-spacing: 0;
`;

const BandSubline = styled.div`
  margin-top: 6px;
  color: #94a3b8;
  font-size: 13px;
`;

const ScopePills = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const ScopePill = styled.div<{ $tone?: ModeTone | 'neutral' }>`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 28px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid ${({ $tone = 'neutral' }) => $tone === 'neutral' ? 'rgba(100, 116, 139, 0.34)' : toneBackground($tone, true)};
  background: ${({ $tone = 'neutral' }) => $tone === 'neutral' ? 'rgba(15, 23, 42, 0.64)' : toneBackground($tone)};
  color: ${({ $tone = 'neutral' }) => $tone === 'neutral' ? '#cbd5e1' : toneColor($tone)};
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  white-space: nowrap;
`;

const InstrumentGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(320px, 0.8fr) minmax(0, 1.2fr);
  gap: 18px;
  align-items: stretch;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const SearchDock = styled.div`
  border: 1px solid rgba(51, 65, 85, 0.78);
  border-radius: 12px;
  background: rgba(2, 6, 23, 0.24);
  padding: 16px;
  min-width: 0;

  input {
    width: 100%;
    box-sizing: border-box;
  }
`;

const SearchHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
`;

const SearchTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #e2e8f0;
  font-weight: 800;
  font-size: 13px;
`;

const SearchScope = styled.div`
  color: #64748b;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const FacilityCard = styled.div`
  border: 1px solid rgba(45, 212, 191, 0.2);
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(8, 47, 73, 0.34) 0%, rgba(15, 23, 42, 0.7) 54%, rgba(20, 83, 45, 0.16) 100%);
  padding: 16px;
  min-width: 0;
`;

const FacilityHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 760px) {
    flex-direction: column;
  }
`;

const FacilityIdentity = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
`;

const FacilityIcon = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(14, 165, 233, 0.12);
  border: 1px solid rgba(14, 165, 233, 0.26);
  color: #7dd3fc;
  flex-shrink: 0;
`;

const FacilityName = styled.div`
  color: #f8fafc;
  font-size: 17px;
  line-height: 1.25;
  font-weight: 800;
  overflow-wrap: anywhere;
`;

const FacilityMeta = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 6px;
  color: #94a3b8;
  font-size: 12px;
`;

const FacilityMetaCode = styled.span`
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  color: #bfdbfe;
`;

const FacilityMetrics = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(120px, 1fr));
  gap: 10px;

  @media (max-width: 980px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const LoanMetric = styled.div`
  padding: 12px;
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.66);
  border: 1px solid rgba(71, 85, 105, 0.38);
  min-width: 0;
`;

const LoanMetricLabel = styled.div`
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 5px;
`;

const LoanMetricValue = styled.div`
  color: #f8fafc;
  font-size: 14px;
  line-height: 1.2;
  font-weight: 800;
  overflow-wrap: anywhere;
`;

const FundingPanel = styled.div`
  min-width: 230px;
  width: 260px;
  padding: 12px;
  border-radius: 12px;
  background: rgba(2, 6, 23, 0.3);
  border: 1px solid rgba(71, 85, 105, 0.42);
  flex-shrink: 0;
`;

const FundingHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  color: #cbd5e1;
  font-size: 12px;
  font-weight: 800;
  margin-bottom: 10px;
`;

const FundingTrack = styled.div`
  height: 8px;
  border-radius: 999px;
  background: rgba(51, 65, 85, 0.72);
  overflow: hidden;
`;

const FundingFill = styled.div<{ $pct: number }>`
  width: ${({ $pct }) => `${Math.min(100, Math.max(0, $pct))}%`};
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #22c55e 0%, #38bdf8 100%);
`;

const FundingAmounts = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 9px;
  color: #94a3b8;
  font-size: 11px;
  font-weight: 700;
`;

const ReferenceBadge = styled.div`
  min-width: 200px;
  padding: 12px;
  border-radius: 12px;
  background: rgba(2, 6, 23, 0.3);
  border: 1px dashed rgba(125, 211, 252, 0.34);
  color: #7dd3fc;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const ExposureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(130px, 1fr));
  gap: 10px;
  margin-top: 14px;

  @media (max-width: 1080px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const ModeGroupLabel = styled.div`
  margin: 14px 0 8px;
  color: #64748b;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.11em;
  text-transform: uppercase;

  &:first-of-type {
    margin-top: 0;
  }
`;

const PlaceholderPanel = styled.div`
  display: grid;
  place-items: center;
  min-height: 174px;
  border: 1px dashed rgba(100, 116, 139, 0.44);
  border-radius: 12px;
  background: rgba(15, 23, 42, 0.28);
  color: #64748b;
`;

const PlaceholderInner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  font-weight: 700;
`;

const ModeBar = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 10px;
`;

const ModeTab = styled.button<{ $active: boolean; $tone: ModeTone }>`
  min-height: 72px;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid ${({ $active, $tone }) => $active ? toneBackground($tone, true) : 'rgba(71, 85, 105, 0.44)'};
  background: ${({ $active, $tone }) => $active ? toneBackground($tone, true) : 'rgba(15, 23, 42, 0.58)'};
  color: ${({ $active, $tone }) => $active ? toneColor($tone) : '#cbd5e1'};
  cursor: pointer;
  text-align: left;
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr);
  gap: 10px;
  align-items: center;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;

  &:hover:not(:disabled) {
    border-color: ${({ $tone }) => toneColor($tone)};
    background: ${({ $tone }) => toneBackground($tone, true)};
  }

  &:disabled {
    opacity: 0.48;
    cursor: not-allowed;
  }
`;

const ModeGlyph = styled.div<{ $tone: ModeTone }>`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${({ $tone }) => toneBackground($tone)};
  color: ${({ $tone }) => toneColor($tone)};
`;

const ModeText = styled.div`
  min-width: 0;
`;

const ModeMain = styled.div`
  font-size: 13px;
  font-weight: 900;
  color: #f8fafc;
  margin-bottom: 4px;
`;

const ModeMeta = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  color: #94a3b8;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const ModeDisabledReason = styled.div`
  color: #f59e0b;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.04em;
`;

const PortfolioAssignmentPanel = styled.div<{ $tone: ModeTone; $error?: boolean }>`
  margin-top: 14px;
  display: grid;
  grid-template-columns: minmax(220px, 0.42fr) minmax(280px, 0.58fr);
  gap: 16px;
  align-items: center;
  padding: 14px;
  border-radius: 12px;
  border: 1px solid ${({ $tone, $error }) => $error ? 'rgba(239, 68, 68, 0.4)' : toneBackground($tone, true)};
  background: ${({ $tone, $error }) => $error ? 'rgba(127, 29, 29, 0.14)' : toneBackground($tone)};

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const PortfolioAssignmentHeader = styled.div`
  min-width: 0;
`;

const PortfolioAssignmentTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #e2e8f0;
  font-size: 13px;
  font-weight: 900;
`;

const PortfolioAssignmentMeta = styled.div`
  margin-top: 5px;
  color: #94a3b8;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const PortfolioAssignmentValue = styled.div<{ $error?: boolean }>`
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 13px;
  border-radius: 10px;
  border: 1px solid ${({ $error }) => $error ? 'rgba(239, 68, 68, 0.38)' : 'rgba(51, 65, 85, 0.7)'};
  background: rgba(2, 6, 23, 0.32);
  color: ${({ $error }) => $error ? '#fca5a5' : '#f8fafc'};
  font-size: 13px;
  font-weight: 850;
`;

const PortfolioSourceBadge = styled.span<{ $error?: boolean }>`
  flex: 0 0 auto;
  color: ${({ $error }) => $error ? '#fca5a5' : '#7dd3fc'};
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(320px, 0.85fr);
  gap: 18px;
  align-items: start;

  @media (max-width: 1080px) {
    grid-template-columns: 1fr;
  }
`;

const DetailPanel = styled.div`
  border: 1px solid rgba(51, 65, 85, 0.74);
  border-radius: 12px;
  background: rgba(2, 6, 23, 0.2);
  padding: 16px;
`;

const DetailPanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
`;

const DetailPanelTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #e2e8f0;
  font-size: 13px;
  font-weight: 900;
`;

const SettlementTag = styled.div`
  color: #94a3b8;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const InfoNote = styled.div<{ $error?: boolean }>`
  margin: 0 0 16px;
  padding: 11px 13px;
  border-radius: 10px;
  border: 1px solid ${p => p.$error ? 'rgba(239,68,68,0.36)' : 'rgba(20,184,166,0.26)'};
  background: ${p => p.$error ? 'rgba(127,29,29,0.16)' : 'rgba(20,184,166,0.08)'};
  color: ${p => p.$error ? '#fca5a5' : '#99f6e4'};
  font-size: 12px;
  font-weight: 700;
`;

const ComplianceNoteCard = styled.div`
  margin: 0 0 16px;
  padding: 11px 13px;
  border-radius: 10px;
  border: 1px solid rgba(20, 184, 166, 0.26);
  background: rgba(20, 184, 166, 0.08);
  color: #99f6e4;
  font-size: 12px;
  font-weight: 700;
`;

const ComplianceNoteHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;

  @media (max-width: 720px) {
    flex-direction: column;
  }
`;

const ComplianceNoteTitle = styled.div`
  color: #ccfbf1;
  font-size: 12px;
  font-weight: 800;
`;

const ComplianceNoteMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 8px;
`;

const ComplianceNotePill = styled.span<{ $tone?: 'neutral' | 'warn' | 'danger' | 'success' }>`
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 3px 8px;
  border-radius: 999px;
  border: 1px solid ${({ $tone = 'neutral' }) => {
    switch ($tone) {
      case 'warn': return 'rgba(245, 158, 11, 0.34)';
      case 'danger': return 'rgba(239, 68, 68, 0.34)';
      case 'success': return 'rgba(34, 197, 94, 0.34)';
      default: return 'rgba(100, 116, 139, 0.34)';
    }
  }};
  background: ${({ $tone = 'neutral' }) => {
    switch ($tone) {
      case 'warn': return 'rgba(245, 158, 11, 0.12)';
      case 'danger': return 'rgba(239, 68, 68, 0.12)';
      case 'success': return 'rgba(34, 197, 94, 0.12)';
      default: return 'rgba(15, 23, 42, 0.64)';
    }
  }};
  color: ${({ $tone = 'neutral' }) => {
    switch ($tone) {
      case 'warn': return '#fbbf24';
      case 'danger': return '#fca5a5';
      case 'success': return '#86efac';
      default: return '#cbd5e1';
    }
  }};
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  white-space: nowrap;
`;

const ComplianceNoteList = styled.ul`
  margin: 10px 0 0;
  padding-left: 18px;
  color: #d1fae5;
  font-size: 12px;
  line-height: 1.45;
  font-weight: 600;
`;

const ComplianceNoteDetails = styled.details`
  margin-top: 10px;
  color: #a7f3d0;

  summary {
    cursor: pointer;
    color: #99f6e4;
    font-weight: 800;
    list-style: none;
  }

  summary::-webkit-details-marker {
    display: none;
  }
`;

const ComplianceNoteRaw = styled.pre`
  margin: 8px 0 0;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid rgba(45, 212, 191, 0.18);
  background: rgba(8, 15, 24, 0.55);
  white-space: pre-wrap;
  word-break: break-word;
  font-family: inherit;
  font-size: 11px;
  line-height: 1.45;
  color: #a7f3d0;
`;

const ChannelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 14px;
`;

type ComplianceRuleCheck = {
  ruleName?: string;
  message?: string;
  severity?: string;
  breachAction?: string;
  passed?: boolean;
};

type ComplianceNoteSummary = {
  label: string;
  status?: string;
  mode?: string;
  resultStatus?: string;
  ruleChecks: ComplianceRuleCheck[];
  messages: string[];
  raw: string;
};

const parseComplianceNoteSummary = (
  note?: string | null,
  decision?: string | null,
): ComplianceNoteSummary | null => {
  if (!note) return null;
  const raw = note.trim();
  if (!raw) return null;

  const prefixMatch = raw.match(/^Compliance\s+([A-Z0-9_ -]+)\s*:\s*([\s\S]+)$/i);
  const label = decision?.trim() || prefixMatch?.[1]?.trim() || 'Compliance';
  const body = prefixMatch?.[2]?.trim() || raw;

  let parsed: any = null;
  try {
    parsed = JSON.parse(body);
  } catch {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return {
        label,
        raw,
        messages: [],
        ruleChecks: [],
      };
    }
  }

  const result = parsed?.result && typeof parsed.result === 'object' ? parsed.result : {};
  const ruleChecks = Array.isArray(result.rules_checked)
    ? result.rules_checked.filter(Boolean)
    : Array.isArray(parsed?.rules_checked)
      ? parsed.rules_checked.filter(Boolean)
      : [];
  const errorSource = Array.isArray(result.errors)
    ? result.errors
    : Array.isArray(parsed?.errors)
      ? parsed.errors
      : [];

  const messages = [
    ...ruleChecks
      .filter((rule: ComplianceRuleCheck) => rule?.passed === false)
      .map((rule: ComplianceRuleCheck) => {
        const parts = [rule.ruleName, rule.message].filter(Boolean);
        return parts.join(' — ');
      }),
    ...errorSource.map((entry: any) => {
      if (typeof entry === 'string') return entry;
      if (entry && typeof entry === 'object') {
        return entry.message || entry.ruleName || entry.detail || JSON.stringify(entry);
      }
      return String(entry);
    }),
  ].filter(Boolean);

  return {
    label,
    status: parsed?.status ? String(parsed.status) : undefined,
    mode: parsed?.mode ? String(parsed.mode) : undefined,
    resultStatus: result?.status ? String(result.status) : undefined,
    ruleChecks,
    messages: Array.from(new Set(messages)),
    raw,
  };
};

const CheckboxRow = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #cbd5e1;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;

  input {
    accent-color: #60a5fa;
  }
`;

// ── AXP-133 (LL-FR-14/D15): multi-fund block allocation grid ────────────────
const AllocationGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AllocationTotals = styled.div<{ $state: 'ok' | 'pending' | 'error' }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 800;
  border: 1px solid ${({ $state }) =>
    $state === 'ok' ? 'rgba(52, 211, 153, 0.35)'
      : $state === 'error' ? 'rgba(239, 68, 68, 0.38)'
      : 'rgba(51, 65, 85, 0.7)'};
  background: ${({ $state }) =>
    $state === 'ok' ? 'rgba(52, 211, 153, 0.08)'
      : $state === 'error' ? 'rgba(127, 29, 29, 0.16)'
      : 'rgba(2, 6, 23, 0.32)'};
  color: ${({ $state }) =>
    $state === 'ok' ? '#6ee7b7' : $state === 'error' ? '#fca5a5' : '#94a3b8'};
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (v?: number | null) =>
  v == null ? '—' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);

const num = (v: any, fallback = 0) => {
  const n = typeof v === 'string' ? parseFloat(v.replace(/,/g, '')) : Number(v);
  return isNaN(n) ? fallback : n;
};

const numOrNull = (v: any): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'string' ? parseFloat(v.replace(/,/g, '')) : Number(v);
  return isNaN(n) ? null : n;
};

const formatAmountInput = (value: string | number | null | undefined) => {
  if (value === null || value === undefined || value === '') return '';
  const parsed = parseAmountShorthand(value);
  return parsed > 0 ? formatAmountWithCommas(parsed) : String(value);
};

// F28 input-fidelity guard: a focused numeric/date input must not be mutated by
// a stray scroll — blur before the browser applies the wheel step.
const blurOnWheel = (e: React.WheelEvent<HTMLElement>) => {
  (e.currentTarget as HTMLElement).blur();
};

// ── AXP-133 (LL-FR-14/D15): multi-fund block allocation helpers ─────────────

interface LlAllocationRow {
  portfolioId: string;
  par: string;
}

const fmtExact = (v: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(v);

// Penny-exact comparison — the backend gate is EXACT reconciliation (LL-FR-14.2).
const toCents = (v: number) => Math.round(v * 100);

/** One empty row for the base ticket's Allocation Grid. */
const blankGridRow = { portfolioId: '', portfolioName: '', allocatedAmount: '', notes: '' };

const summarizeLlAllocations = (rows: LlAllocationRow[], blockPar: number) => {
  const active = rows.filter(r => r.portfolioId || r.par.trim() !== '');
  const seen = new Set<string>();
  let total = 0;
  let error = '';
  active.forEach((r, i) => {
    if (!r.portfolioId) error = error || `Row ${i + 1}: select a fund`;
    else if (seen.has(r.portfolioId)) error = error || `Row ${i + 1}: duplicate fund — merge rows`;
    if (r.portfolioId) seen.add(r.portfolioId);
    const par = parseAmountShorthand(r.par);
    if (!(par > 0)) error = error || `Row ${i + 1}: allocated par must be > 0`;
    else total += par;
  });
  if (!error && active.length > 0 && blockPar > 0 && toCents(total) !== toCents(blockPar)) {
    const diff = Math.abs(blockPar - total);
    error = (total < blockPar ? 'Under-allocated by ' : 'Over-allocated by ') + fmtExact(diff)
      + ' — allocations must equal block par exactly (LL-FR-14)';
  }
  return { active, total, error };
};

// ─── Types ────────────────────────────────────────────────────────────────────

type LoanTicketKind = 'private-credit' | 'leveraged-loan';
type PcMode = 'acquire' | 'drawdown' | 'paydown';
// PM/Trader acquisition and transfer modes submit from this screen. Borrower
// servicing events remain visible for orientation, but launch from the shared
// Loan 360 / Loan Book action surface so LL and PC use one governed workflow.
type LlMode = 'primary' | 'secondary' | 'lsta-sell' | 'paydown' | 'drawdown';

interface PrivateFixedIncomeTicketScreenProps {
  ticketKind?: LoanTicketKind;
}

interface LoanCtx {
  id: number;
  loanId: string;
  borrowerName: string;
  currentCommitment: number;
  fundedAmount: number;
  unfundedAmount: number;
  oidPrice?: number;
  spreadBps?: number;
  allInRate?: number;
  // LL facility REFERENCE data (AXP-121/V402) — global, never the fund's.
  originalSize?: number | null;
  globalOutstanding?: number | null;
  outstandingAsOf?: string | null;
  trancheCode?: string | null;
  // LL Phase 2 Slice 1b — facility class drives the strip (RCF/DDTL) acquisition
  // path: a strip assignment carries an undrawn commitment alongside funded par.
  facilityType?: string | null;
  reborrowable?: boolean | null;
  // Portfolio from committed loan (PC only) — LL booking is always explicit (F35)
  portfolioId?: number;
  portfolioName?: string;
}

// FR-2.3 portfolio exposure read model — all zero / "No holdings" before the
// first acquisition posts.
interface LlExposure {
  loaded: boolean;
  fundedPrincipal: number;
  unfundedCommitment: number;
  settledExposure: number;
  pendingBuy: number;
  pendingSell: number;
}

const EMPTY_EXPOSURE: LlExposure = {
  loaded: false,
  fundedPrincipal: 0,
  unfundedCommitment: 0,
  settledExposure: 0,
  pendingBuy: 0,
  pendingSell: 0,
};

interface PositionResolution {
  portfolioId?: number;
  portfolioName?: string;
  fundedAmount?: number;
  marketValue?: number;
  costBasis?: number;
}

// AXP-133 / R2-10a: POST /api/credit/ll/blocks/{parentId}/advance fans a
// lifecycle action out to every ALLOCATION_CHILD and reports per-child
// outcomes (children advance independently — one failing does not stop its
// siblings). The parent mirrors the least-advanced child's stage.
interface LlBlockAdvanceChildResult {
  tradeId: number;
  portfolioId: number | null;
  success: boolean;
  status: string | null;
  error?: string;
}

interface LlBlockAdvanceResponse {
  parentId: number;
  action: string;
  children: LlBlockAdvanceChildResult[];
  parentStatus: string | null;
}

interface ModeMetaConfig {
  label: string;
  eventType: 'ACQUIRE' | 'DISPOSE' | 'DRAWDOWN' | 'REPAY';
  displayEventType?: 'ACQUIRE' | 'DISPOSE' | 'DRAWDOWN' | 'PAYDOWN';
  side: 'BUY' | 'SELL';
  tone: ModeTone;
  Icon: LucideIcon;
  /** LL: channel is FIXED per tile — the ticket sends it explicitly (D4). */
  channel?: 'PRIMARY' | 'SECONDARY';
  group?: 'transfer' | 'ops';
  disabled?: boolean;
  disabledReason?: string;
}

const pcModeMeta: Record<PcMode, ModeMetaConfig> = {
  acquire: { label: 'Acquire', eventType: 'ACQUIRE', side: 'BUY', tone: 'buy', Icon: BadgeDollarSign },
  drawdown: { label: 'Drawdown', eventType: 'DRAWDOWN', side: 'BUY', tone: 'draw', Icon: ArrowDownToLine },
  paydown: { label: 'Paydown', eventType: 'REPAY', displayEventType: 'PAYDOWN', side: 'SELL', tone: 'pay', Icon: ArrowUpFromLine },
};

const llModeMeta: Record<LlMode, ModeMetaConfig> = {
  primary: {
    label: 'Primary Allocation', eventType: 'ACQUIRE', side: 'BUY', tone: 'buy',
    Icon: Layers, channel: 'PRIMARY', group: 'transfer',
  },
  secondary: {
    label: 'Secondary Assignment', eventType: 'ACQUIRE', side: 'BUY', tone: 'buy',
    Icon: Banknote, channel: 'SECONDARY', group: 'transfer',
  },
  'lsta-sell': {
    label: 'LSTA Sell', eventType: 'DISPOSE', side: 'SELL', tone: 'sell',
    Icon: CircleDollarSign, channel: 'SECONDARY', group: 'transfer',
  },
  paydown: {
    label: 'Paydown', eventType: 'REPAY', displayEventType: 'PAYDOWN', side: 'SELL', tone: 'pay',
    Icon: ArrowUpFromLine, group: 'ops',
    disabled: true, disabledReason: 'Use Loan 360 servicing',
  },
  drawdown: {
    label: 'Drawdown', eventType: 'DRAWDOWN', side: 'BUY', tone: 'draw',
    Icon: ArrowDownToLine, group: 'ops',
    disabled: true, disabledReason: 'Use Loan 360 servicing',
  },
};

const extractRows = (payload: any): any[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.data)) return payload.data;
  // /api/ibor/replay envelope: { mode, positions: [...], summary } (R2-11a)
  if (Array.isArray(payload?.positions)) return payload.positions;
  return [];
};

const resolvePositionPortfolio = async (securityId: number): Promise<PositionResolution> => {
  try {
    const res = await axios.get('/api/ibor/replay', {
      params: {
        validAsOf: getTodayDate(),
        knowledgeAsOf: 'now',
        basis: 'TRADE_DATE',
      },
    });
    const positions = extractRows(res.data);
    const position = positions.find((p: any) =>
      Number(p.securityId) === Number(securityId) &&
      (num(p.quantity) > 0 || num(p.parAmount) > 0 || num(p.marketValue) > 0)
    );
    if (position) {
      return {
        portfolioId: position.portfolioId,
        portfolioName: position.portfolioName || position.portfolioCode,
        fundedAmount: num(position.quantity) || num(position.parAmount) || num(position.currentFace),
        marketValue: num(position.marketValue),
        costBasis: num(position.costBasis),
      };
    }
  } catch {
    // Fall through to the legacy latest-position lookup.
  }

  try {
    const res = await axios.get(`/api/positions/security/${securityId}`);
    const positions = extractRows(res.data);
    const position = positions.find((p: any) => num(p.quantity) > 0 || num(p.marketValue) > 0) || positions[0];
    if (!position) return {};
    return {
      portfolioId: position.portfolioId,
      portfolioName: position.portfolioName || position.portfolioCode,
      fundedAmount: num(position.quantity) || num(position.parAmount) || num(position.currentFace),
      marketValue: num(position.marketValue),
      costBasis: num(position.costBasis),
    };
  } catch {
    return {};
  }
};

const fundedFromIbor = (resolved: PositionResolution, fallback: number) => {
  const funded = num(resolved.fundedAmount);
  return funded > 0 ? funded : fallback;
};

const unfundedFromCommitment = (commitment: number, funded: number, fallback: number) => {
  if (commitment > 0) return Math.max(commitment - funded, 0);
  return fallback;
};

// Resolve a portfolio's display name from its id. Needed because
// /api/private-credit/loans serializes the committed portfolio as an id-only
// lazy proxy (no name). For a commitment-only facility there's also no
// fact_positions row to source the name from, so the ticket would otherwise
// show "Pending facility context" even though the portfolioId is resolved
// and the drawdown will post correctly (MF-5).
const resolvePortfolioName = async (portfolioId?: number): Promise<string | undefined> => {
  if (!portfolioId) return undefined;
  try {
    const res = await axios.get('/api/dimensions/portfolios');
    const rows = Array.isArray(res.data) ? res.data : res.data?.content || [];
    const p = rows.find((x: any) => x.id === portfolioId);
    return p ? (p.portfolioCode || p.portfolioName) : undefined;
  } catch {
    return undefined;
  }
};

// FR-2.3 / FR-4: the selected portfolio's OWN exposure to the tranche — never
// facility balances. Funded Principal from the IBOR replay is TRADE basis
// (POSTED trades, settled or not). Pending Buy/Sell = POSTED trades whose
// settlement has not completed; DRAFT..EXECUTED open orders carry ZERO
// exposure per the FR-4 state table and are never counted as pending (R2-11b).
const fetchLlExposure = async (securityId: number, portfolioId: number): Promise<LlExposure> => {
  let fundedPrincipal = 0;
  try {
    const res = await axios.get('/api/ibor/replay', {
      params: { validAsOf: getTodayDate(), knowledgeAsOf: 'now', basis: 'TRADE_DATE' },
    });
    const position = extractRows(res.data).find((p: any) =>
      Number(p.securityId) === Number(securityId) && Number(p.portfolioId) === Number(portfolioId));
    if (position) {
      fundedPrincipal = num(position.quantity) || num(position.parAmount) || num(position.currentFace);
    }
  } catch { /* read model unavailable — show zeros, never fabricate */ }

  let pendingBuy = 0;
  let pendingSell = 0;
  try {
    const res = await axios.get('/api/trade-transactions', {
      params: { securityId, portfolioId, size: 200 },
    });
    for (const t of extractRows(res.data)) {
      // FR-4: only POSTED trades carry exposure; pending = POSTED and unsettled.
      if (String(t.status) !== 'POSTED') continue;
      const settlementStatus = t.settlementStatus ? String(t.settlementStatus).toUpperCase() : null;
      // AXP-132 settlement stamps: PENDING_SETTLEMENT / FAILED are unsettled;
      // a null stamp on an LL POSTED trade predates the settlement engine and
      // is treated as unsettled unless an actual settle date was recorded.
      const unsettled = settlementStatus === null
        ? !t.actualSettleDate
        : settlementStatus === 'PENDING_SETTLEMENT' || settlementStatus === 'FAILED';
      if (!unsettled) continue;
      const par = num(t.quantity) || num(t.amount);
      const et = String(t.eventType || '').toUpperCase();
      if (et === 'ACQUIRE' || (et === '' && t.transactionType === 'BUY')) pendingBuy += par;
      else if (et === 'DISPOSE' || (et === '' && t.transactionType === 'SELL')) pendingSell += par;
    }
  } catch { /* pending unavailable — leave zeros */ }

  // Phase 1 books fully-funded TLB strips only (drawdown lifecycle is Phase 2),
  // so the fund's unfunded commitment on this tranche is zero until then.
  const unfundedCommitment = 0;
  // FRD §2.3: settled exposure EXCLUDES pending items (shown separately) —
  // back the unsettled buys out of / add the unsettled sells back into the
  // TRADE-basis funded figure, floored at zero.
  const settledPrincipal = Math.max(fundedPrincipal - pendingBuy + pendingSell, 0);
  return {
    loaded: true,
    fundedPrincipal,
    unfundedCommitment,
    settledExposure: settledPrincipal + unfundedCommitment,
    pendingBuy,
    pendingSell,
  };
};

const getStoredUserFullName = (): string => {
  if (typeof window === 'undefined') return '';
  try {
    const storedUser = window.localStorage.getItem('user');
    if (!storedUser) return '';
    const parsed = JSON.parse(storedUser);
    return typeof parsed?.fullName === 'string' ? parsed.fullName : '';
  } catch {
    return '';
  }
};

// ADR 0009: a direct-lending trade settles on its trade date (T+0) on the facility currency's
// calendar. The server has the last word on the settle date. (Leveraged-loan transfers ask
// /api/credit/ll/settlement-convention instead: T+7, or T+20 below 80.)
const pcSettleQuery = (tradeDate: string, currency?: string) =>
  ({ tradeDate, ticketType: 'PRIVATE_CREDIT', convention: 'T+0', currency: currency || 'USD' });

/** The provisional settle date at once, then the server's, unless the form has moved on meanwhile. */
const syncPcSettleDate = (setFormData: React.Dispatch<React.SetStateAction<any>>, tradeDate: string, currency?: string) => {
  const query = pcSettleQuery(tradeDate, currency);
  const provisional = provisionalSettleDate(query);
  setFormData((prev: any) => ({ ...prev, settleDate: provisional }));
  void resolveSettleDate(query).then(settleDate => setFormData((prev: any) =>
    prev.tradeDate === tradeDate && (prev.currency || 'USD') === query.currency && prev.settleDate === provisional
      ? { ...prev, settleDate } : prev));
};

/** Runs once when a new ticket opens. */
const OnOpen: React.FC<{ run: () => void }> = ({ run }) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { run(); }, []);
  return null;
};

// ─── Component ────────────────────────────────────────────────────────────────

const PrivateFixedIncomeTicketScreen: React.FC<PrivateFixedIncomeTicketScreenProps> = ({ ticketKind = 'private-credit' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user: authUser } = useAuth();
  const isPc = ticketKind === 'private-credit';
  const defaultSubmittedBy = authUser?.fullName || getStoredUserFullName();
  const routeState = location.state as any;
  const routePrefill = routeState?.ticketPrefill || {};
  const routePickedSecurity = routeState?.pickedSecurity as PickedSecurity | undefined;
  const urlCusip = (searchParams.get('cusip') || '').trim();
  // A trade opened from the blotter keeps its stored settle date.
  const openingTrade = !!(routeState?.transactionId || routeState?.id);
  // F-BD-007: why the LSTA settlement endpoint refused the trade date (blank when it answered).
  const [llSettleError, setLlSettleError] = useState('');
  const llSettleRequest = useRef(0);

  // Shared picker state (only one branch active at a time)
  const [pickedSecurity, setPickedSecurity] = useState<PickedSecurity | null>(() => {
    if (routePickedSecurity?.id) return routePickedSecurity;
    if (routePrefill?.securityId) {
      return {
        id: Number(routePrefill.securityId),
        cusip: routePrefill.cusip || undefined,
        securityName: routePrefill.security || routePrefill.securityName || routePrefill.cusip || undefined,
      };
    }
    return null;
  });
  const [loanCtx, setLoanCtx] = useState<LoanCtx | null>(null);
  const [loanCtxLoading, setLoanCtxLoading] = useState(false);
  const [loadedTradeAudit, setLoadedTradeAudit] = useState<any | null>(null);
  const [postingLineage, setPostingLineage] = useState<any | null>(null);
  const [lateBookingApprovalBusy, setLateBookingApprovalBusy] = useState<'compliance' | 'operations' | null>(null);
  const [lateBookingApprovalError, setLateBookingApprovalError] = useState('');
  const complianceNoteSummary = useMemo(() => {
    const note = postingLineage?.compliance?.notes || loadedTradeAudit?.complianceNotes;
    return parseComplianceNoteSummary(note, postingLineage?.compliance?.decision);
  }, [loadedTradeAudit?.complianceNotes, postingLineage?.compliance?.decision, postingLineage?.compliance?.notes]);

  const authRole = String(authUser?.role || '').toUpperCase();
  const canApproveLateBookingCompliance = ['ADMIN', 'PLATFORM_ADMIN', 'COMPLIANCE_OFFICER'].includes(authRole);
  const canApproveLateBookingOperations = ['ADMIN', 'PLATFORM_ADMIN', 'OPERATIONS_ANALYST', 'MIDDLE_OFFICE_ADMIN'].includes(authRole);
  const lateBookingActive = !isPc && loadedTradeAudit?.lateBooking === true;
  const lateBookingFullyApproved = lateBookingActive
    && !!loadedTradeAudit?.lateBookingComplianceApprovedAt
    && !!loadedTradeAudit?.lateBookingOpsApprovedAt;

  const approveLateBooking = async (tradeId: number, lane: 'compliance' | 'operations') => {
    setLateBookingApprovalBusy(lane);
    setLateBookingApprovalError('');
    try {
      const response = await axios.post(
        `/api/credit/ll/trade-confirmations/${tradeId}/approve-${lane}`
      );
      const approval = response.data || {};
      setLoadedTradeAudit((current: any) => ({
        ...current,
        lateBooking: approval.lateBooking === true,
        lateBookingComplianceApprovedBy: approval.complianceApprovedBy,
        lateBookingComplianceApprovedAt: approval.complianceApprovedAt,
        lateBookingOpsApprovedBy: approval.opsApprovedBy,
        lateBookingOpsApprovedAt: approval.opsApprovedAt,
        status: approval.status || current?.status,
      }));
    } catch (error: any) {
      setLateBookingApprovalError(
        error.response?.data?.error || error.response?.data || error.message || 'Approval failed'
      );
    } finally {
      setLateBookingApprovalBusy(null);
    }
  };

  useEffect(() => {
    if (pickedSecurity?.id || !urlCusip) return;
    let cancelled = false;
    axios.get('/api/dimensions/securities/search-by-class', {
      params: { q: urlCusip, assetClass: isPc ? 'PRIVATE_CREDIT' : 'LEVERAGED_LOAN', limit: 20 },
    }).then(res => {
      if (cancelled) return;
      const rows: PickedSecurity[] = Array.isArray(res.data) ? res.data : [];
      const match = rows.find(r => String(r.cusip || '').toUpperCase() === urlCusip.toUpperCase()) || rows[0];
      if (match?.id) setPickedSecurity(match);
    }).catch(() => {
      // The visible ticket identifier still carries the URL CUSIP; picker error
      // state is handled by normal ticket validation if the user attempts save.
    });
    return () => { cancelled = true; };
  }, [isPc, pickedSecurity?.id, urlCusip]);

  // PC modes: acquire | drawdown | paydown
  const [pcMode, setPcMode] = useState<PcMode>('drawdown');
  // LL Phase 1 tiles (AXP-127): default to Secondary Assignment (most common flow)
  const [llMode, setLlMode] = useState<LlMode>('secondary');

  // LL Phase 2 Slice 1b — is the picked facility an RCF/DDTL strip? A strip
  // secondary assignment acquires an undrawn commitment alongside the funded par
  // (drives the "Undrawn Commitment" input + unfundedCommitment payload field).
  const llIsStrip = !isPc && (
    loanCtx?.facilityType === 'REVOLVER'
    || loanCtx?.facilityType === 'DELAYED_DRAW'
    || loanCtx?.reborrowable === true
  );

  // AXP-133 (LL-FR-14/D15): optional multi-fund allocation on the Primary
  // Allocation / Secondary Assignment tiles. Rows submit as `allocations`;
  // the backend creates a BLOCK_PARENT (never posts) + one ALLOCATION_CHILD
  // per fund with pro-rata economics. Grid off = single-portfolio path,
  // unchanged.
  const [llUseAllocations, setLlUseAllocations] = useState(false);
  // The fund rows live in the base ticket's Allocation Grid (one editor for every ticket); this is a
  // mirror of them in LL-FR-14 terms, kept current by onAllocationsChange, and baseAllocations lets
  // the panel's own opt-in checkbox and a reopened block drive the grid.
  const [llAllocations, setLlAllocations] = useState<LlAllocationRow[]>([]);
  const baseAllocations = useRef<{ setAllocations: (rows: any) => void; setUseAllocations: (on: boolean) => void } | null>(null);
  const handleBaseAllocations = useCallback((rows: Array<{ portfolioId: string; allocatedAmount: string }>, on: boolean) => {
    setLlAllocations(rows.map(r => ({ portfolioId: r.portfolioId || '', par: r.allocatedAmount || '' })));
    setLlUseAllocations(on);
  }, []);
  const setBlockMode = (on: boolean, seed: boolean) => {
    setLlUseAllocations(on);
    baseAllocations.current?.setUseAllocations(on);
    if (on && seed) baseAllocations.current?.setAllocations([blankGridRow]);
  };

  const isLlAllocationModeActive = useCallback((_blockPar?: number) => {
    if (!(llMode === 'primary' || llMode === 'secondary')) return false;
    if (llUseAllocations) return true;
    return false;
  }, [llMode, llUseAllocations]);


  // F35: LL booking portfolio is an EXPLICIT selection; tracked here so the
  // FR-2.3 exposure read can key off it outside the render prop.
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<number | null>(null);
  const [llExposure, setLlExposure] = useState<LlExposure>(EMPTY_EXPOSURE);

  // R2-10a: blockRole of the loaded transaction (BLOCK_PARENT / ALLOCATION_CHILD).
  // A multi-fund LL submission creates a BLOCK_PARENT whose generic /send and
  // /execute endpoints are rejected server-side — those actions must fan out to
  // the children via POST /api/credit/ll/blocks/{id}/advance.
  const [llBlockRole, setLlBlockRole] = useState<string | null>(null);
  const llBlockParentActive = !isPc && (
    llBlockRole === 'BLOCK_PARENT' ||
    (llUseAllocations && (llMode === 'primary' || llMode === 'secondary'))
  );

  const advanceLlBlock = useCallback(async (
    transactionId: number,
    action: 'submitForPreClear' | 'approvePreClear' | 'send' | 'execute',
    helpers: TicketWorkflowOverrideHelpers,
  ): Promise<boolean> => {
    // NOTE: the actor is derived server-side from the authenticated principal —
    // no executedBy in the body.
    const res = await axios.post<LlBlockAdvanceResponse>(
      `/api/credit/ll/blocks/${transactionId}/advance`,
      { action },
    );
    const children = res.data?.children || [];
    const succeeded = children.filter(c => c.success);
    const failed = children.filter(c => !c.success);
    const fallbackStatus = {
      submitForPreClear: 'PRECLEAR_PENDING',
      approvePreClear: 'PRECLEAR_APPROVED',
      send: 'SENT',
      execute: 'EXECUTED',
    } as const;
    helpers.setTransactionStatus(
      normalizeTicketStatus(res.data?.parentStatus) || fallbackStatus[action]);
    const verb = {
      submitForPreClear: 'submitted for pre-clear',
      approvePreClear: 'pre-clear approved',
      send: 'sent',
      execute: 'executed',
    }[action];
    if (succeeded.length > 0) {
      helpers.notifySuccess(
        `Block ${verb}: ${succeeded.length}/${children.length} fund allocation${children.length > 1 ? 's' : ''} advanced.`);
    }
    if (failed.length > 0) {
      const detail = failed
        .map(f => `#${f.tradeId}${f.error ? ` — ${f.error}` : ''}`)
        .join('; ');
      helpers.setErrors({
        submit: `${failed.length} of ${children.length} allocation children failed to ${action}: ${detail}`,
      });
    }
    return true;
  }, []);

  // R4-6c (Scenario 1B omnibus): saving a reopened LL BLOCK_PARENT must NEVER
  // go through the generic PUT /api/trade-transactions/{id} — the backend
  // rejects block rows there, and the old fall-through silently assigned the
  // first portfolio without creating children. Instead:
  //   • unallocated parent (omnibus late-booking import) → POST the fund grid
  //     to /api/credit/ll/blocks/{id}/allocate (LLBlockAllocationController,
  //     R3-12b) — body is a trade DTO carrying allocations
  //     [{portfolioId, allocatedAmount}], same shape as /validate;
  //   • already-allocated parent → immutable here; lifecycle actions fan out
  //     to the children via /advance.
  // The reopen DTO only carries creation-time allocation audit rows (an
  // import-created parent has none), so the block-state endpoint
  // GET /api/credit/ll/blocks/{id} ({children:[{tradeId, portfolioId,
  // portfolioName, par, status, ...}]}) is the authoritative child check.
  const allocateLlBlockOnSave = useCallback(async (
    transactionId: number | null,
    transactionData: any,
    helpers: TicketWorkflowOverrideHelpers,
  ): Promise<boolean> => {
    // Creation and ordinary trades keep the generic path (multi-row creates
    // already route to createAllocatedBlock server-side). Also intercept when
    // the grid is active on an EDIT: an in-session multi-fund create leaves
    // editingTransactionId pointing at the BLOCK_PARENT while llBlockRole is
    // only stamped on reopen.
    if (isPc || transactionId == null
        || !(llBlockRole === 'BLOCK_PARENT' || (llUseAllocations && (llMode === 'primary' || llMode === 'secondary')))) {
      return false;
    }
    let blockState: any;
    try {
      blockState = (await axios.get(`/api/credit/ll/blocks/${transactionId}`)).data;
    } catch (err: any) {
      // 404 = not a block parent (e.g. a single-row grid degenerated into an
      // ordinary single-portfolio trade at creation) — generic PUT is correct.
      if (err?.response?.status === 404) return false;
      throw err;
    }
    if (blockState?.blockRole !== 'BLOCK_PARENT') return false;
    const existingChildren: any[] = Array.isArray(blockState?.children) ? blockState.children : [];
    if (existingChildren.length > 0) {
      helpers.setErrors({
        submit: `Block ${transactionId} already has ${existingChildren.length} allocation `
          + `child${existingChildren.length > 1 ? 'ren' : ''} — the block parent is the external record and is not `
          + 'editable after allocation; lifecycle actions fan out to the child trades (LL-FR-14).',
      });
      return true; // handled — never send the generic PUT for a block parent
    }
    const allocations = Array.isArray(transactionData?.allocations) && transactionData.allocations.length > 0
      ? transactionData.allocations
      : null;
    if (!allocations) {
      helpers.setErrors({
        llAllocations: 'This omnibus block requires an explicit fund allocation — add allocation rows '
          + 'summing exactly to block par (LL-FR-14.2).',
      });
      return true;
    }
    const res = await axios.post(`/api/credit/ll/blocks/${transactionId}/allocate`, { allocations });
    const children: any[] = Array.isArray(res.data?.children) ? res.data.children : [];
    // Refresh the Allocation Grid from the authoritative per-child state.
    baseAllocations.current?.setUseAllocations(true);
    baseAllocations.current?.setAllocations(children.length > 0
      ? children.map((c: any) => ({
          portfolioId: c.portfolioId != null ? String(c.portfolioId) : '',
          portfolioName: c.portfolioName || '',
          allocatedAmount: formatAmountInput(c.par),
          notes: '',
        }))
      : [blankGridRow]);
    helpers.setTransactionStatus(normalizeTicketStatus(res.data?.status) || 'DRAFT');
    const detail = children
      .map((c: any) => `#${c.tradeId} → ${c.portfolioName || `portfolio ${c.portfolioId}`} (${fmt(num(c.par))}${c.status ? `, ${c.status}` : ''})`)
      .join('; ');
    helpers.notifySuccess(
      `Block allocated: ${children.length} fund allocation child${children.length > 1 ? 'ren' : ''} created — ${detail}`);
    // Subsequent lifecycle actions must fan out via /advance (R2-10a).
    setLlBlockRole('BLOCK_PARENT');
    return true;
  }, [isPc, llBlockRole, llUseAllocations, llMode]);

  // R2-10a: BaseTicketScreen extension point — per-ticket workflow overrides.
  // Returning false falls through to the generic /api/trade-transactions path.
  // R3-2 (LL-FR-14): pre-clear ALSO fans out to the children — each portfolio
  // child independently pre-clears; the generic /api/pre-clear endpoints would
  // pre-clear only the parent and leave the children DRAFT, letting them send
  // without any child-level compliance check. Compliance approval then happens
  // per child via the same advance {action:'approvePreClear'}, which the
  // controller gates to compliance roles (R2-12).
  const workflowOverrides = useMemo<TicketWorkflowOverrides>(() => ({
    saveDraft: allocateLlBlockOnSave,
    submitPreClear: async (transactionId, helpers) =>
      llBlockParentActive ? advanceLlBlock(transactionId, 'submitForPreClear', helpers) : false,
    approvePreClear: async (transactionId, _notes, helpers) =>
      llBlockParentActive ? advanceLlBlock(transactionId, 'approvePreClear', helpers) : false,
    send: async (transactionId, helpers) =>
      llBlockParentActive ? advanceLlBlock(transactionId, 'send', helpers) : false,
    execute: async (transactionId, _executionData, helpers) =>
      llBlockParentActive ? advanceLlBlock(transactionId, 'execute', helpers) : false,
  }), [llBlockParentActive, advanceLlBlock, allocateLlBlockOnSave]);

  // ─── PC loan context ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!isPc || !pickedSecurity) { setLoanCtx(null); return; }
    setLoanCtxLoading(true);
    axios.get('/api/private-credit/loans').then(async res => {
      const loans: any[] = Array.isArray(res.data) ? res.data : [];
      const match = loans.find((l: any) => l.security?.id === pickedSecurity.id || l.securityId === pickedSecurity.id);
      if (match) {
        let portfolioId = match.portfolio?.id || match.portfolioId;
        let portfolioName = match.portfolio?.portfolioName || match.portfolioName;
        const resolved = await resolvePositionPortfolio(pickedSecurity.id);
        if (!portfolioId || !portfolioName) {
          portfolioId = portfolioId || resolved.portfolioId;
          portfolioName = portfolioName || resolved.portfolioName;
        }
        // Commitment-only facility: portfolioId came back (loan.portfolio id) but
        // no name (id-only lazy proxy) and no position to source it — look it up so
        // the committed facility shows a name instead of "Pending facility context" (MF-5).
        if (portfolioId && !portfolioName) {
          portfolioName = await resolvePortfolioName(portfolioId);
        }
        const currentCommitment = num(match.currentCommitment);
        const fundedAmount = fundedFromIbor(resolved, num(match.fundedAmount));
        setLoanCtx({
          id: match.id,
          loanId: match.loanId,
          borrowerName: match.borrowerName,
          currentCommitment,
          fundedAmount,
          unfundedAmount: unfundedFromCommitment(currentCommitment, fundedAmount, num(match.unfundedAmount)),
          oidPrice: match.oidPrice ? num(match.oidPrice) : undefined,
          portfolioId,
          portfolioName,
        });
      } else {
        setLoanCtx(null);
      }
    }).catch(() => setLoanCtx(null)).finally(() => setLoanCtxLoading(false));
  }, [pickedSecurity, isPc]);

  // ─── LL loan context — facility REFERENCE data only (FR-2.3) ───────────────
  // The facility's Original Size / Current Outstanding are the borrower's
  // global numbers. The fund's own exposure comes from the FR-2.3 read below
  // and NEVER from these balances (F37).
  useEffect(() => {
    if (isPc || !pickedSecurity) { setLoanCtx(null); return; }
    setLoanCtxLoading(true);
    axios.get('/api/credit/loans').then(res => {
      const loans: any[] = Array.isArray(res.data) ? res.data : [];
      const match = loans.find((l: any) => l.security?.id === pickedSecurity.id || l.securityId === pickedSecurity.id);
      if (!match) { setLoanCtx(null); return; }
      const currentCommitment = num(match.currentCommitment);
      setLoanCtx({
        id: match.id,
        loanId: match.loanId,
        borrowerName: match.borrowerName,
        currentCommitment,
        fundedAmount: num(match.fundedAmount),
        unfundedAmount: num(match.unfundedAmount),
        oidPrice: match.oidPrice ? num(match.oidPrice) : undefined,
        spreadBps: match.spreadBps != null ? num(match.spreadBps) : undefined,
        allInRate: match.allInRate != null ? num(match.allInRate) : undefined,
        originalSize: numOrNull(match.originalSize) ?? (currentCommitment > 0 ? currentCommitment : null),
        globalOutstanding: numOrNull(match.globalOutstanding),
        outstandingAsOf: match.outstandingAsOf || null,
        trancheCode: match.trancheCode || null,
        facilityType: match.facilityType || null,
        reborrowable: match.reborrowable ?? null,
      });
    }).catch(() => setLoanCtx(null)).finally(() => setLoanCtxLoading(false));
  }, [pickedSecurity, isPc]);

  // ─── LL portfolio exposure (FR-2.3) — keyed on the EXPLICIT selection ──────
  useEffect(() => {
    if (isPc || !pickedSecurity || !selectedPortfolioId) {
      setLlExposure(EMPTY_EXPOSURE);
      return;
    }
    let cancelled = false;
    fetchLlExposure(pickedSecurity.id, selectedPortfolioId).then(exposure => {
      if (!cancelled) setLlExposure(exposure);
    });
    return () => { cancelled = true; };
  }, [pickedSecurity, selectedPortfolioId, isPc]);

  // ─── Initial form state ────────────────────────────────────────────────────
  const openTradeDate = getTodayDate(calendarsFor(isPc ? 'PRIVATE_CREDIT' : 'LEVERAGED_LOAN', 'USD'));
  const initialFormData = {
    transactionType: 'BUY' as const,
    assetManager: '', portfolioManager: '',
    assetClass: isPc ? 'Private Fixed Income' : 'LEVERAGED_LOAN',
    assetSubClass: isPc ? 'Private Debt' : 'Leveraged Loans',
    security: routePrefill.security || '', identifier: routePrefill.identifier || routePrefill.cusip || urlCusip || '', portfolio: '',
    // FR-6.1 (BWIC construction): a won bid opens this ticket with its economics
    // prefilled — one human affirmation books the DRAFT, never an auto-book.
    amount: routePrefill.parAmount != null ? String(routePrefill.parAmount) : '',
    price: routePrefill.cleanPrice != null ? String(routePrefill.cleanPrice) : '',
    tradeDate: openTradeDate,
    settleDate: isPc ? provisionalSettleDate(pcSettleQuery(openTradeDate)) : '',
    submittedBy: defaultSubmittedBy, currency: 'USD', executedBy: '',
    notes: routePrefill.sourceBidIntentId ? `BWIC bid intent #${routePrefill.sourceBidIntentId}` : '',
    valuationMethod: isPc ? 'Mark-to-Model' : 'Standard',
    pricingSource: isPc ? 'Internal' : 'IHS Markit',
    settlementConvention: isPc ? 'T+0' : '',
    liquidityFlag: false,
    // ── LL two-dimension acquisition attributes (AXP-127, LL-FR-3) ──
    llTransferForm: 'ASSIGNMENT',      // DIRECT_LENDER | ASSIGNMENT (PARTICIPATION = Phase 2, disabled)
    llAssignmentOnly: false,
    llArranger: '',
    llAllocationSource: '',
    llRequested: '',
    llCircled: '',
    llAwarded: '',
    llIssuePrice: '',
    llExpectedClose: '',
    llSeller: routePrefill.counterparty || '',
    llBuyer: '',
    llPurchasedAccrued: '',
    llAssignmentFee: '',
    llFeePayer: 'BUYER',
    llEvidenceId: '',
    llUnfundedCommitment: '',
  };

  // ─── Validate form ─────────────────────────────────────────────────────────
  // Why the trade date field is empty although a date was typed (a weekend or holiday), with the next business day.
  const [tradeDateProblem, setTradeDateProblem] = useState<{ problem: string; fix: { iso: string; label: string } | null } | null>(null);
  const validateForm = (formData: any, action: string): { [key: string]: string } => {
    const errors: { [key: string]: string } = {};
    if (!pickedSecurity) { errors.identifier = `Select a ${isPc ? 'PC' : 'LL'} facility using the search above`; }
    // Par Amount = the funded/drawn par. A PRIMARY strip (RCF/DDTL) allocation is
    // typically UNDRAWN at close (drawn = 0), with the commitment carried on
    // Awarded — so a blank/zero Par Amount is valid there (treated as 0 drawn).
    const primaryStripUndrawnOk = !isPc && llMode === 'primary' && llIsStrip;
    if (!formData.amount?.trim() && !primaryStripUndrawnOk) { errors.amount = 'Amount is required'; }
    if (!formData.tradeDate) {
      // A typed weekend or holiday leaves the field empty on purpose; say that, not "required".
      errors.tradeDate = tradeDateProblem
        ? `${tradeDateProblem.problem}${tradeDateProblem.fix ? ` — use ${tradeDateProblem.fix.label}` : ''}`
        : 'Trade date is required';
    }
    if (action === 'execute' && !formData.executedBy?.trim()) { errors.executedBy = 'Executed By is required'; }

    if (isPc) {
      if (pcMode === 'acquire' && !formData.portfolio?.trim()) {
        errors.portfolio = 'Portfolio is required';
      }
      if ((pcMode === 'drawdown' || pcMode === 'paydown') && !loanCtx?.portfolioId) {
        errors.portfolio = 'No existing position found — post the initial trade first';
      }
      return errors;
    }

    // ── Leveraged Loan (AXP-127) ────────────────────────────────────────────
    // AXP-133: with the multi-fund grid active the booking portfolios are the
    // allocation rows (F35 explicitness per row) — the single select is moot.
    const allocationsActive = isLlAllocationModeActive(parseAmountShorthand(formData.amount));
    // F35: portfolio is ALWAYS an explicit selection.
    if (!allocationsActive && !formData.portfolio?.trim()) {
      errors.portfolio = 'Portfolio is required — select the booking portfolio explicitly';
    }
    if (allocationsActive) {
      const summary = summarizeLlAllocations(llAllocations, parseAmountShorthand(formData.amount));
      if (summary.active.length === 0) {
        errors.llAllocations = 'Add at least one fund allocation (fund + par)';
      } else if (summary.error) {
        errors.llAllocations = summary.error;
      }
    }
    // F33: trade date must be a business day.
    if (formData.tradeDate && !isBusinessDay(parseDateString(formData.tradeDate),
      calendarsFor(isPc ? 'PRIVATE_CREDIT' : 'LEVERAGED_LOAN', formData.currency))) {
      errors.tradeDate = 'Trade date falls on a weekend / US holiday — pick a business day';
    }

    // Borrower events use the shared Loan 360 / Loan Book servicing workflow,
    // never the portfolio-scoped acquisition ticket writer.
    if (llMode === 'drawdown') {
      errors.amount = 'Use Drawdown from Loan 360 or the Loan Book';
      return errors;
    }
    if (llMode === 'paydown') {
      errors.amount = 'Use Paydown from Loan 360 or the Loan Book';
      return errors;
    }
    if (llMode === 'primary') {
      if (!formData.llArranger?.trim()) errors.llArranger = 'Arranger is required for a primary allocation';
      if (!formData.llRequested?.trim()) errors.llRequested = 'Requested amount is required';
      if (!formData.llCircled?.trim()) errors.llCircled = 'Circled amount is required';
      if (!formData.llAwarded?.trim()) errors.llAwarded = 'Awarded amount is required';
      if (!formData.llIssuePrice?.trim()) errors.llIssuePrice = 'Issue price is required';
      if (!formData.llExpectedClose) errors.llExpectedClose = 'Expected close date is required';
    }
    if (llMode === 'secondary') {
      if (!formData.llSeller?.trim()) errors.llSeller = 'Seller / assignor of record is required (LL-FR-3.3)';
      if (!formData.price?.trim()) errors.price = 'Transfer price is required';
    }
    if (llMode === 'lsta-sell') {
      if (!formData.price?.trim()) errors.price = 'Transfer price is required';
      if (!formData.llBuyer?.trim()) errors.llBuyer = 'Buyer / assignee counterparty is required';
    }
    return errors;
  };

  // ─── Build transaction ─────────────────────────────────────────────────────
  const buildTransactionData = async (formData: any, dimensionData: any): Promise<any> => {
    if (!pickedSecurity) throw new Error('Select a facility first');
    const faceAmount = parseAmountShorthand(formData.amount);

    if (isPc) {
      if (pcMode === 'acquire') {
        const portfolio = dimensionData.portfolios.find((p: any) => p.portfolioName === formData.portfolio?.trim());
        if (!portfolio) throw new Error(`Portfolio "${formData.portfolio}" not found`);
        const price = num(formData.price, 100);
        return {
          ticketType: 'PRIVATE_CREDIT', eventType: 'ACQUIRE', transactionType: 'BUY',
          securityId: pickedSecurity.id, cusip: pickedSecurity.cusip,
          // #55: BUY convention — amount carries PAR/FACE; the backend derives
          // principal/net (= face x price/100) itself. Sending the discounted
          // figure here double-discounts principal server-side.
          quantity: faceAmount, price, amount: faceAmount,
          portfolioId: portfolio.id, tradeDate: formData.tradeDate, settleDate: formData.settleDate || formData.tradeDate,
          currency: 'USD', assetSubClass: 'Private Debt', valuationMethod: 'Mark-to-Model',
          pricingSource: 'Internal', settlementConvention: 'T+0', status: 'DRAFT',
          notes: formData.notes || `PC secondary acquisition: ${loanCtx?.borrowerName || pickedSecurity.securityName}`,
          createdBy: formData.submittedBy, executedBy: formData.executedBy || null,
        };
      }
      if (!loanCtx?.portfolioId) throw new Error('No committed portfolio. Post the ACQUIRE trade first.');
      const oidPrice = loanCtx.oidPrice && loanCtx.oidPrice > 0 ? loanCtx.oidPrice : 100;
      if (pcMode === 'drawdown') {
        return {
          ticketType: 'PRIVATE_CREDIT', eventType: 'DRAWDOWN', transactionType: 'BUY',
          securityId: pickedSecurity.id, cusip: pickedSecurity.cusip,
          quantity: faceAmount, price: oidPrice, amount: faceAmount, // #55: BUY amount = face

          portfolioId: loanCtx.portfolioId, tradeDate: formData.tradeDate, settleDate: formData.settleDate || formData.tradeDate,
          currency: 'USD', assetSubClass: 'Private Debt', valuationMethod: 'Mark-to-Model',
          pricingSource: 'Internal', settlementConvention: 'T+0', status: 'DRAFT',
          notes: formData.notes || `Drawdown ${fmt(faceAmount)} on ${formData.tradeDate} — ${loanCtx.borrowerName}`,
          createdBy: formData.submittedBy, executedBy: formData.executedBy || null,
        };
      }
      // paydown
      return {
        ticketType: 'PRIVATE_CREDIT', eventType: 'REPAY', transactionType: 'SELL',
        securityId: pickedSecurity.id, cusip: pickedSecurity.cusip,
        quantity: faceAmount, price: 100, amount: faceAmount,
        portfolioId: loanCtx.portfolioId, tradeDate: formData.tradeDate, settleDate: formData.settleDate || formData.tradeDate,
        currency: 'USD', assetSubClass: 'Private Debt', valuationMethod: 'Mark-to-Model',
        pricingSource: 'Internal', settlementConvention: 'T+0', status: 'DRAFT',
        notes: formData.notes || `Paydown ${fmt(faceAmount)} on ${formData.tradeDate} — ${loanCtx.borrowerName}`,
        createdBy: formData.submittedBy, executedBy: formData.executedBy || null,
      };
    }

    // ── Leveraged Loan (AXP-127, LL-FR-3): explicit two-dimension payloads ──
    // AXP-133 (LL-FR-14/D15): with the multi-fund grid active the payload
    // carries `allocations` and no single portfolioId — the backend creates a
    // BLOCK_PARENT (portfolio-less external record, never posts) plus one
    // ALLOCATION_CHILD per fund with pro-rata economics.
    const allocationsActive = isLlAllocationModeActive(faceAmount);
    const allocationSummary = allocationsActive
      ? summarizeLlAllocations(llAllocations, faceAmount)
      : null;
    if (allocationSummary && allocationSummary.error) throw new Error(allocationSummary.error);
    const blockAllocations = allocationSummary && allocationSummary.active.length > 0
      ? allocationSummary.active.map(r => ({
          portfolioId: Number(r.portfolioId),
          allocatedAmount: parseAmountShorthand(r.par),
        }))
      : null;

    // F35: the booking portfolio is ALWAYS the ticket's explicit selection.
    const portfolio = dimensionData.portfolios.find((p: any) => p.portfolioName === formData.portfolio?.trim());
    if (!portfolio && !blockAllocations) {
      throw new Error(`Portfolio "${formData.portfolio}" not found — select the booking portfolio explicitly`);
    }
    const llTransferMode = llMode === 'secondary' || llMode === 'lsta-sell';
    if (llTransferMode && (!formData.settleDate || !formData.settlementConvention)) {
      throw new Error('Authoritative LSTA settlement terms are unavailable — resolve the backend convention before saving');
    }

    const llCommon = {
      ticketType: 'LEVERAGED_LOAN',
      securityId: pickedSecurity.id,
      cusip: pickedSecurity.cusip,
      identifierType: 'CUSIP',
      identifierValue: pickedSecurity.cusip,
      portfolioId: blockAllocations ? null : portfolio.id,
      ...(blockAllocations ? { allocations: blockAllocations } : {}),
      tradeDate: formData.tradeDate,
      settleDate: llTransferMode ? formData.settleDate : (formData.settleDate || formData.tradeDate),
      currency: 'USD', assetSubClass: 'Leveraged Loans', valuationMethod: 'Standard',
      pricingSource: 'IHS Markit',
      settlementConvention: llTransferMode
        ? formData.settlementConvention
        : (formData.settlementConvention || (llMode === 'primary' ? 'PRIMARY_CLOSE' : 'T+0')),
      status: 'DRAFT',
      createdBy: formData.submittedBy, executedBy: formData.executedBy || null,
      externalEvidenceId: formData.llEvidenceId?.trim() || null,
    };

    if (llMode === 'primary') {
      const issuePrice = num(formData.llIssuePrice, 0);
      return {
        ...llCommon,
        eventType: 'ACQUIRE', transactionType: 'BUY',
        marketChannel: 'PRIMARY', loanAcquisitionType: 'PRIMARY',
        intendedTransferForm: 'DIRECT_LENDER',
        actualTransferForm: 'DIRECT_LENDER',
        assignmentOnlyElection: null,
        arrangerName: formData.llArranger?.trim(),
        allocationSource: formData.llAllocationSource?.trim() || null,
        requestedAmount: parseAmountShorthand(formData.llRequested),
        circledAmount: parseAmountShorthand(formData.llCircled),
        awardedAmount: parseAmountShorthand(formData.llAwarded),
        issuePrice,
        expectedCloseDate: formData.llExpectedClose,
        // #55: BUY amount = face; funds at the issue/OID price.
        // Slice 1b/1c (AXP-146): a PRIMARY RCF/DDTL allocation is a commitment
        // strip. Par Amount = the FUNDED par drawn at close; Awarded = the total
        // commitment. Undrawn = Awarded − funded (floored at 0):
        //   funded == 0 (undrawn at close) → send amount = Awarded so it passes the
        //     amount > 0 validation (the PC commitment pattern); quantity = 0 so the
        //     backend routes to the zero-funded commitment-only path.
        //   funded  > 0 (partially drawn)  → send amount/quantity = the funded par;
        //     the backend posts a REAL funded position + strip attrs (commitment =
        //     funded + unfunded). unfundedCommitment carries the undrawn portion.
        // A non-strip TLB is a real funded allocation: amount = Par Amount, no
        // undrawn commitment.
        quantity: faceAmount,
        amount: llIsStrip
          ? (faceAmount > 0 ? faceAmount : (parseAmountShorthand(formData.llAwarded) || 0))
          : faceAmount,
        price: issuePrice,
        unfundedCommitment: llIsStrip
          ? Math.max((parseAmountShorthand(formData.llAwarded) || 0) - faceAmount, 0)
          : null,
        notes: formData.notes || `Primary allocation via ${formData.llArranger}: ${loanCtx?.borrowerName || pickedSecurity.securityName}`,
      };
    }

    if (llMode === 'secondary') {
      const price = num(formData.price, 0);
      const purchasedAccrued = parseAmountShorthand(formData.llPurchasedAccrued) || 0;
      const isParticipation = formData.llTransferForm === 'PARTICIPATION';
      const grantorPortfolioId = isParticipation && formData.llGrantorPortfolioId
        ? (parseInt(String(formData.llGrantorPortfolioId), 10) || null)
        : null;
      return {
        ...llCommon,
        eventType: 'ACQUIRE', transactionType: 'BUY',
        marketChannel: 'SECONDARY', loanAcquisitionType: 'SECONDARY',
        // A participation is NOT an assignment: the seller (grantor) stays lender
        // of record; the participant holds a synthetic position vs. the grantor.
        intendedTransferForm: isParticipation ? 'PARTICIPATION' : 'ASSIGNMENT',
        actualTransferForm: isParticipation ? 'PARTICIPATION' : 'ASSIGNMENT',
        participationFeeBps: isParticipation ? (parseFloat(String(formData.llParticipationFeeBps)) || 0) : null,
        participationConsentSacred: isParticipation ? (formData.llParticipationConsent !== false) : null,
        participationGrantorPortfolioId: grantorPortfolioId,
        assignmentOnlyElection: !isParticipation && !!formData.llAssignmentOnly,
        sellerCounterparty: formData.llSeller?.trim(),
        counterparty: formData.llSeller?.trim(),
        cleanPrice: price,
        purchasedAccruedInterest: purchasedAccrued,
        accruedInterest: purchasedAccrued,
        assignmentFee: parseAmountShorthand(formData.llAssignmentFee) || null,
        assignmentFeePayer: formData.llFeePayer || null,
        // #55: BUY amount = face (backend computes cash).
        // Slice 1b — on an RCF/DDTL strip, amount/quantity are the FUNDED par and
        // unfundedCommitment carries the undrawn portion (commitment = funded +
        // unfunded). Sent only for strip facilities; null on fully-funded paper.
        quantity: faceAmount, amount: faceAmount, price,
        unfundedCommitment: llIsStrip
          ? (parseAmountShorthand(formData.llUnfundedCommitment) || null)
          : null,
        notes: formData.notes || `LSTA secondary assignment from ${formData.llSeller}: ${loanCtx?.borrowerName || pickedSecurity.securityName}`,
      };
    }

    if (llMode === 'lsta-sell') {
      const price = num(formData.price, 0);
      return {
        ...llCommon,
        eventType: 'DISPOSE', transactionType: 'SELL',
        marketChannel: 'SECONDARY', loanAcquisitionType: 'SECONDARY',
        intendedTransferForm: 'ASSIGNMENT',
        actualTransferForm: 'ASSIGNMENT',
        assignmentOnlyElection: !!formData.llAssignmentOnly,
        counterparty: formData.llBuyer?.trim(),
        cleanPrice: price,
        assignmentFee: parseAmountShorthand(formData.llAssignmentFee) || null,
        assignmentFeePayer: formData.llFeePayer || null,
        // R3-8 / BUG-001: LEVERAGED_LOAN is a par-quantity ticket — the backend
        // resets quantity = amount at execution (isParQuantityTicket), so amount
        // MUST be PAR/FACE, never proceeds. Sending proceeds (par × price/100)
        // shrank a $5M sell at 98.5 to $4.925M par and double-applied the price
        // at settlement. Proceeds are derived server-side from par × clean price.
        quantity: faceAmount, price, amount: faceAmount,
        notes: formData.notes || `LSTA sell to ${formData.llBuyer}: ${loanCtx?.borrowerName || pickedSecurity.securityName}`,
      };
    }

    if (llMode === 'drawdown') {
      throw new Error('Use Drawdown from Loan 360 or the Loan Book');
    }

    // R4-8b: the generic REPAY submission branch is REMOVED — LL paydowns are
    // facility-level borrower events that apply pro-rata to every holder via
    // the agent-notice tranche-event pipeline (FRD §5 / FR-5), never a
    // portfolio-scoped /api/trade-transactions ticket.
    throw new Error('Use Paydown from Loan 360 or the Loan Book');
  };

  // ─── Load transaction (reopen from blotter) ────────────────────────────────
  const loadTransactionData = (transaction: any) => {
    setLoadedTradeAudit(transaction);
    if (transaction?.id) {
      axios.get(`/api/trade-transactions/${transaction.id}/posting-lineage`)
        .then(response => setPostingLineage(response.data))
        .catch(() => setPostingLineage(null));
    }
    const et = (transaction.eventType || '').toUpperCase();
    const isLlPrimaryTicket = !isPc && (
      String(transaction.marketChannel || '').toUpperCase() === 'PRIMARY'
      || String(transaction.loanAcquisitionType || '').toUpperCase() === 'PRIMARY'
    );
    if (transaction.securityId && transaction.cusip) {
      setPickedSecurity({ id: transaction.securityId, cusip: transaction.cusip, securityName: transaction.securityName || transaction.cusip });
    }
    if (isPc) {
      if (et === 'DRAWDOWN') setPcMode('drawdown');
      else if (et === 'PAYDOWN' || et === 'REPAY') setPcMode('paydown');
      else setPcMode('acquire');
    } else {
      const channel = (transaction.marketChannel || transaction.loanAcquisitionType || '').toUpperCase();
      if (et === 'DRAWDOWN') setLlMode('drawdown');
      else if (et === 'PAYDOWN' || et === 'REPAY') setLlMode('paydown');
      else if (et === 'DISPOSE') setLlMode('lsta-sell');
      else setLlMode(channel === 'PRIMARY' ? 'primary' : 'secondary');
      if (transaction.portfolioId) setSelectedPortfolioId(transaction.portfolioId);
      // R2-10a: remember the block role so Send/Execute route BLOCK_PARENTs to
      // the block advance endpoint instead of the generic lifecycle actions.
      setLlBlockRole(transaction.blockRole || null);
      // AXP-133: reopening a BLOCK_PARENT restores the multi-fund grid.
      const reopenedAllocations = Array.isArray(transaction.allocations) ? transaction.allocations : [];
      if (reopenedAllocations.length > 1 || transaction.blockRole === 'BLOCK_PARENT') {
        // The base screen loads the rows into its Allocation Grid and switches it on.
        // R4-6c: an UNALLOCATED parent (omnibus late-booking import) reopens
        // with no allocation rows — seed a blank grid row so the PM can enter
        // the fund split; save then POSTs /api/credit/ll/blocks/{id}/allocate.
        setBlockMode(true, reopenedAllocations.length === 0);
      } else {
        setBlockMode(false, false);
      }
    }
    return {
      ...initialFormData,
      transactionType: transaction.transactionType || 'BUY',
      assetManager: transaction.assetManagerName || transaction.assetManager || '',
      portfolioManager: transaction.portfolioManagerName || transaction.portfolioManager || '',
      portfolio: transaction.portfolioName || transaction.portfolio || '',
      security: transaction.securityName || transaction.cusip || '',
      identifier: transaction.cusip || transaction.isin || '',
      amount: transaction.quantity != null ? formatAmountInput(transaction.quantity) : formatAmountInput(transaction.amount),
      price: transaction.price != null ? String(transaction.price) : '',
      tradeDate: transaction.tradeDate || getTodayDate(),
      settleDate: transaction.settleDate || transaction.tradeDate || getTodayDate(),
      submittedBy: transaction.createdBy || defaultSubmittedBy,
      currency: transaction.currency || 'USD',
      executedBy: transaction.executedBy || '',
      notes: transaction.notes || '',
      valuationMethod: transaction.valuationMethod || initialFormData.valuationMethod,
      pricingSource: transaction.pricingSource || initialFormData.pricingSource,
      settlementConvention: transaction.settlementConvention || initialFormData.settlementConvention,
      // LL two-dimension attributes (V404) — restore on reopen
      llTransferForm: isLlPrimaryTicket ? 'DIRECT_LENDER' : (transaction.intendedTransferForm || 'ASSIGNMENT'),
      llAssignmentOnly: isLlPrimaryTicket ? false : !!transaction.assignmentOnlyElection,
      llArranger: transaction.arrangerName || '',
      llAllocationSource: transaction.allocationSource || '',
      llRequested: formatAmountInput(transaction.requestedAmount),
      llCircled: formatAmountInput(transaction.circledAmount),
      llAwarded: formatAmountInput(transaction.awardedAmount),
      llIssuePrice: transaction.issuePrice != null ? String(transaction.issuePrice) : '',
      llExpectedClose: transaction.expectedCloseDate || '',
      llSeller: transaction.sellerCounterparty || '',
      llBuyer: (transaction.eventType === 'DISPOSE' ? transaction.counterparty : '') || '',
      llPurchasedAccrued: formatAmountInput(transaction.purchasedAccruedInterest),
      llAssignmentFee: formatAmountInput(transaction.assignmentFee),
      llUnfundedCommitment: formatAmountInput(transaction.unfundedCommitment),
      llFeePayer: transaction.assignmentFeePayer || 'BUYER',
      llEvidenceId: transaction.externalEvidenceId || '',
    };
  };

  // ─── Render helpers ────────────────────────────────────────────────────────
  const title = isPc ? 'Direct Lending Trade Entry' : 'Leveraged Loan Trade Entry';

  return (
    <BaseTicketScreen
      title={title}
      assetClass={isPc ? 'Private Fixed Income' : 'LEVERAGED_LOAN'}
      initialFormData={initialFormData}
      validateForm={validateForm}
      buildTransactionData={buildTransactionData}
      loadTransactionData={loadTransactionData}
      workflowOverrides={workflowOverrides}
      lateBookingControl={{
        active: lateBookingActive,
        fullyApproved: lateBookingFullyApproved,
      }}
      hidePortfolioField={true}
      onAllocationsChange={handleBaseAllocations}
      aiSuggestionsLabel={isPc ? 'Get Direct Lending Ideas' : 'Get Loan Trade Ideas'}
      aiSuggestionsLoadingLabel="Getting ideas..."
      aiSuggestionsAssetClass={isPc ? 'Private Fixed Income' : 'Leveraged Loans'}
      aiSuggestionsPortfolioId={isPc ? (loanCtx?.portfolioId || null) : selectedPortfolioId}
      aiSuggestionsSubMessage={isPc
        ? 'Analyzing committed facility exposure and direct lending opportunities'
        : 'Analyzing loan exposure and syndicated credit opportunities'}
    >
      {({ formData, setFormData, errors, dimensionData, transactionStatus, editingTransactionId, handleInputChange, handleInputBlur, isFieldDisabled, setAllocations, setUseAllocations }) => {
        baseAllocations.current = { setAllocations, setUseAllocations };
        const locked = !!transactionStatus && transactionStatus !== 'DRAFT';
        const persistedTicket = editingTransactionId !== null;
        const modeLocked = locked || persistedTicket;
        const mode = isPc ? pcMode : llMode;
        const modeMeta = isPc ? pcModeMeta[pcMode] : llModeMeta[llMode];
        const modeDisplayEvent = modeMeta.displayEventType || modeMeta.eventType;
        const ModeIcon = modeMeta.Icon;
        const llAllocationPanelActive = !isPc
          && isLlAllocationModeActive(parseAmountShorthand(formData.amount));

        const applyServerSettlementConvention = async (tradeDate: string, cleanPrice: string) => {
          const request = ++llSettleRequest.current;
          setLlSettleError('');
          if (!tradeDate) return;
          const requestedPrice = cleanPrice || '';
          try {
            const response = await axios.get('/api/credit/ll/settlement-convention', {
              params: {
                tradeDate,
                cleanPrice: requestedPrice.trim() ? Number(requestedPrice) : 100,
              },
            });
            setFormData((prev: any) => prev.tradeDate === tradeDate
              && String(prev.price || '') === requestedPrice ? {
              ...prev,
              settleDate: response.data.expectedSettlementDate,
              settlementConvention: response.data.convention,
            } : prev);
          } catch (error: any) {
            // F-BD-007: a trade date the loan market is closed on comes back 400 with the
            // reason and the next business day; say so at the field instead of a silent blank.
            if (request === llSettleRequest.current) {
              const refusal = error?.response?.status === 400 ? error.response.data : null;
              const reason = typeof refusal?.error === 'string' ? refusal.error : '';
              setLlSettleError(reason
                ? (refusal.nextBusinessDay && !reason.includes(refusal.nextBusinessDay)
                    ? `${reason}. The next business day is ${refusal.nextBusinessDay}.`
                    : reason)
                : 'LSTA settlement terms could not be resolved; the settle date stays blank until they are.');
            }
            // Fail closed. The ticket must never invent a T+7 date when the
            // authoritative backend may classify the loan as T+20 distressed.
            setFormData((prev: any) => prev.tradeDate === tradeDate
              && String(prev.price || '') === requestedPrice ? {
              ...prev,
              settleDate: '',
              settlementConvention: '',
            } : prev);
          }
        };

        const setLlModeAndDefaults = (nextMode: LlMode) => {
          setLlMode(nextMode);
          // AXP-133: the multi-fund grid only applies to acquisitions
          // (Primary Allocation / Secondary Assignment tiles).
          if (nextMode !== 'primary' && nextMode !== 'secondary') {
            setBlockMode(false, false);
          }
          const meta = llModeMeta[nextMode];
          setFormData((prev: any) => ({
            ...prev,
            transactionType: meta.side,
            amount: '',
            // Primary allocations are direct-lender-at-close; transfer forms are secondary mechanics.
            llTransferForm: nextMode === 'primary' ? 'DIRECT_LENDER' : 'ASSIGNMENT',
            llAssignmentOnly: nextMode === 'primary' ? false : prev.llAssignmentOnly,
            // The backend owns clean-price settlement classification and its
            // business-day calendar; keep transfers unset until it responds.
            settleDate: (nextMode === 'secondary' || nextMode === 'lsta-sell')
              ? ''
              : nextMode === 'primary'
                ? (prev.llExpectedClose || prev.tradeDate)
                : prev.tradeDate,
          }));
          if (nextMode === 'secondary' || nextMode === 'lsta-sell') {
            void applyServerSettlementConvention(formData.tradeDate, formData.price);
          }
        };

        const handleLlTradeDateChange = (value: string) => {
          setFormData((prev: any) => ({
            ...prev,
            tradeDate: value,
            settleDate: (llMode === 'secondary' || llMode === 'lsta-sell')
              ? ''
              : llMode === 'primary'
                ? (prev.llExpectedClose || value)
                : value,
          }));
          if (llMode === 'secondary' || llMode === 'lsta-sell') {
            void applyServerSettlementConvention(value, formData.price);
          }
        };

        const handlePortfolioChange = (portfolioName: string) => {
          handleInputChange('portfolio', portfolioName);
          if (!isPc) {
            const match = (dimensionData.portfolios || []).find((p: any) => p.portfolioName === portfolioName);
            setSelectedPortfolioId(match ? match.id : null);
          }
        };

        // PC keeps its silent facility/holding portfolio resolution; LL is
        // always an explicit selection (F35).
        const needsManualPortfolio = isPc ? pcMode === 'acquire' : true;
        const isTransferMode = isPc ? pcMode === 'acquire' : llModeMeta[llMode].group === 'transfer';
        const economicsBandTitle = !isPc && llMode === 'primary'
          ? 'Primary allocation terms'
          : isTransferMode
            ? 'Transfer terms'
            : `${modeMeta.label} terms`;
        const fundedPct = loanCtx?.currentCommitment
          ? (loanCtx.fundedAmount / loanCtx.currentCommitment) * 100
          : 0;
        const instrumentTitle = loanCtx?.borrowerName || pickedSecurity?.securityName || 'Awaiting instrument';
        const instrumentCode = pickedSecurity?.cusip || loanCtx?.loanId || 'No CUSIP';
        const amountLabel = isTransferMode
          ? 'Par Amount (USD)*'
          : mode === 'drawdown'
            ? 'Draw Amount (USD)*'
            : 'Paydown Amount (USD)*';
        const amountPlaceholder = !isPc
          ? (mode === 'paydown'
              ? `Max settled: ${fmt(llExposure.settledExposure)}`
              : 'e.g. 10,000,000')
          : mode === 'drawdown'
            ? `Max unfunded: ${fmt(loanCtx?.unfundedAmount)}`
            : mode === 'paydown'
              ? `Max funded: ${fmt(loanCtx?.fundedAmount)}`
              : 'e.g. 10,000,000';
        const portfolioResolutionError = isPc && !needsManualPortfolio && !!(pickedSecurity && loanCtx && !loanCtx.portfolioId);
        const portfolioResolutionValue = loanCtx?.portfolioName
          || (portfolioResolutionError ? 'No existing position' : 'Pending facility context');
        const noLlHoldings = llExposure.loaded
          && llExposure.fundedPrincipal <= 0 && llExposure.pendingBuy <= 0 && llExposure.pendingSell <= 0;
        const llSettleRefused = !isPc && (llMode === 'secondary' || llMode === 'lsta-sell')
          && !formData.settleDate && !!llSettleError;

        const llTransferTiles = (['primary', 'secondary', 'lsta-sell'] as LlMode[]).map(m => ({ key: m, ...llModeMeta[m] }));
        const llOpsTiles = (['paydown', 'drawdown'] as LlMode[]).map(m => ({ key: m, ...llModeMeta[m] }));

        const renderLlTile = ({ key, label, eventType, displayEventType, side, tone, Icon, channel, disabled, disabledReason }: any) => (
          <ModeTab
            key={key}
            type="button"
            $active={llMode === key}
            $tone={tone}
            disabled={modeLocked || !pickedSecurity || !!disabled}
            title={disabled ? disabledReason : modeLocked ? 'Trade event is locked after draft creation' : undefined}
            onClick={() => setLlModeAndDefaults(key as LlMode)}
          >
            <ModeGlyph $tone={tone}>
              <Icon size={17} />
            </ModeGlyph>
            <ModeText>
              <ModeMain>{label}</ModeMain>
              {disabled ? (
                <ModeDisabledReason>{disabledReason}</ModeDisabledReason>
              ) : (
                <ModeMeta>
                  <span>{displayEventType || eventType}</span>
                  <span>{side}</span>
                  {channel && <span>{channel}</span>}
                </ModeMeta>
              )}
            </ModeText>
          </ModeTab>
        );

        return (
          <CreditWorkspace>
            {/* F-BD-003: a new ticket shows its settle date at once. LL transfers ask the LSTA
                convention endpoint (the price defaults to 100 until one is typed). */}
            {!openingTrade && <OnOpen run={() => {
              if (isPc) syncPcSettleDate(setFormData, formData.tradeDate, formData.currency);
              else if (llMode === 'secondary' || llMode === 'lsta-sell') void applyServerSettlementConvention(formData.tradeDate, formData.price);
            }} />}
            <WorkspaceBand>
              <BandHeader>
                <BandTitleGroup>
                  <BandEyebrow>
                    <Search size={14} />
                    {isPc ? 'Direct lending facility' : 'Leveraged loan tranche'}
                  </BandEyebrow>
                  <BandTitle>{instrumentTitle}</BandTitle>
                  <BandSubline>
                    {pickedSecurity ? instrumentCode : isPc ? 'Bilateral facilities and club deals' : 'Syndicated loan facilities'}
                  </BandSubline>
                </BandTitleGroup>
                <ScopePills>
                  <ScopePill $tone={modeMeta.tone}><ModeIcon size={13} /> {modeDisplayEvent}</ScopePill>
                  <ScopePill $tone={modeMeta.side === 'SELL' ? 'sell' : 'buy'}>{modeMeta.side}</ScopePill>
                  {!isPc && modeMeta.channel && <ScopePill $tone="neutral">{modeMeta.channel}</ScopePill>}
                </ScopePills>
              </BandHeader>

              <InstrumentGrid>
                <SearchDock>
                  <SearchHeader>
                    <SearchTitle>
                      <Layers size={15} />
                      Instrument
                    </SearchTitle>
                    <SearchScope>{isPc ? 'Direct loan' : 'LSTA loan'}</SearchScope>
                  </SearchHeader>
                  <SecurityPicker
                    label={isPc ? 'Facility (CUSIP / Borrower)*' : 'Loan (CUSIP / Borrower)*'}
                    assetClass={isPc ? 'PRIVATE_CREDIT' : 'LEVERAGED_LOAN'}
                    placeholder={isPc ? 'Search by borrower name or CUSIP...' : 'Search by issuer or CUSIP...'}
                    notFoundLabel={isPc ? 'Define new direct lending loan' : 'Define new LL loan'}
                    onDefineNew={() => navigate(isPc ? '/reference-data?tab=credit-loans' : '/reference-data?tab=credit-loans&assetClass=LL')}
                    disabled={locked}
                    selected={pickedSecurity}
                    error={errors.identifier}
                    onSelect={sec => {
                      setPickedSecurity(sec);
                      setFormData((prev: any) => ({ ...prev, security: sec.securityName || sec.cusip, identifier: sec.cusip || '' }));
                    }}
                    onClear={() => {
                      setPickedSecurity(null);
                      setLoanCtx(null);
                      setLlExposure(EMPTY_EXPOSURE);
                      setFormData((prev: any) => ({ ...prev, security: '', identifier: '', amount: '' }));
                    }}
                  />
                </SearchDock>

                {loanCtx && !loanCtxLoading ? (
                  <FacilityCard>
                    <FacilityHeader>
                      <FacilityIdentity>
                        <FacilityIcon>
                          {isPc ? <Building2 size={19} /> : <Landmark size={19} />}
                        </FacilityIcon>
                        <div>
                          <FacilityName>{loanCtx.borrowerName}</FacilityName>
                          <FacilityMeta>
                            <FacilityMetaCode>{pickedSecurity?.cusip || loanCtx.loanId}</FacilityMetaCode>
                            <span>{loanCtx.loanId}</span>
                            {!isPc && loanCtx.trancheCode && <span>{loanCtx.trancheCode}</span>}
                            {isPc && loanCtx.portfolioName && <span>Committed To: {loanCtx.portfolioName}</span>}
                          </FacilityMeta>
                        </div>
                      </FacilityIdentity>
                      {isPc ? (
                        <FundingPanel>
                          <FundingHeader>
                            <span>Funded</span>
                            <span>{Number.isFinite(fundedPct) ? `${Math.round(fundedPct)}%` : '0%'}</span>
                          </FundingHeader>
                          <FundingTrack>
                            <FundingFill $pct={fundedPct} />
                          </FundingTrack>
                          <FundingAmounts>
                            <span>{fmt(loanCtx.fundedAmount)}</span>
                            <span>{fmt(loanCtx.currentCommitment)}</span>
                          </FundingAmounts>
                        </FundingPanel>
                      ) : (
                        // FR-2.3: these numbers are the borrower's GLOBAL facility —
                        // never the fund's commitment or unfunded (F37).
                        <ReferenceBadge>
                          <Landmark size={14} />
                          Facility reference data — not fund exposure
                        </ReferenceBadge>
                      )}
                    </FacilityHeader>

                    <FacilityMetrics>
                      {isPc ? (
                        <>
                          <LoanMetric>
                            <LoanMetricLabel>Commitment</LoanMetricLabel>
                            <LoanMetricValue>{fmt(loanCtx.currentCommitment)}</LoanMetricValue>
                          </LoanMetric>
                          <LoanMetric>
                            <LoanMetricLabel>Unfunded</LoanMetricLabel>
                            <LoanMetricValue style={{ color: '#7dd3fc' }}>{fmt(loanCtx.unfundedAmount)}</LoanMetricValue>
                          </LoanMetric>
                        </>
                      ) : (
                        <>
                          <LoanMetric>
                            <LoanMetricLabel>Original Size (Facility)</LoanMetricLabel>
                            <LoanMetricValue>{fmt(loanCtx.originalSize)}</LoanMetricValue>
                          </LoanMetric>
                          <LoanMetric>
                            <LoanMetricLabel>
                              Current Outstanding (Global{loanCtx.outstandingAsOf ? ` · ${loanCtx.outstandingAsOf}` : ''})
                            </LoanMetricLabel>
                            <LoanMetricValue style={{ color: '#7dd3fc' }}>
                              {loanCtx.globalOutstanding != null ? fmt(loanCtx.globalOutstanding) : 'Awaiting agent evidence'}
                            </LoanMetricValue>
                          </LoanMetric>
                        </>
                      )}
                      <LoanMetric>
                        <LoanMetricLabel>OID Price</LoanMetricLabel>
                        <LoanMetricValue>{loanCtx.oidPrice != null ? loanCtx.oidPrice : '100'}</LoanMetricValue>
                      </LoanMetric>
                      <LoanMetric>
                        <LoanMetricLabel>{isPc ? 'Valuation' : 'Spread / Rate'}</LoanMetricLabel>
                        <LoanMetricValue>
                          {isPc
                            ? formData.valuationMethod || 'Mark-to-Model'
                            : loanCtx.spreadBps != null
                              ? `${loanCtx.spreadBps} bps${loanCtx.allInRate != null ? ` / ${loanCtx.allInRate}%` : ''}`
                              : 'IHS Markit'}
                        </LoanMetricValue>
                      </LoanMetric>
                    </FacilityMetrics>

                    {!isPc && (
                      <>
                        <BandEyebrow style={{ marginTop: 16 }}>
                          <Wallet size={13} />
                          {selectedPortfolioId
                            ? `Portfolio exposure — ${formData.portfolio || 'selected portfolio'}`
                            : 'Portfolio exposure — select a portfolio below'}
                        </BandEyebrow>
                        {selectedPortfolioId && noLlHoldings && (
                          <InfoNote style={{ margin: '0 0 4px' }}>
                            No holdings — this portfolio has no exposure to the tranche yet. Book a Primary
                            Allocation or Secondary Assignment to acquire.
                          </InfoNote>
                        )}
                        <ExposureGrid>
                          <LoanMetric>
                            <LoanMetricLabel>Funded Principal</LoanMetricLabel>
                            <LoanMetricValue>{fmt(llExposure.fundedPrincipal)}</LoanMetricValue>
                          </LoanMetric>
                          <LoanMetric>
                            <LoanMetricLabel>Unfunded Commitment</LoanMetricLabel>
                            <LoanMetricValue>{fmt(llExposure.unfundedCommitment)}</LoanMetricValue>
                          </LoanMetric>
                          <LoanMetric>
                            <LoanMetricLabel>Total Settled Investor Exposure</LoanMetricLabel>
                            <LoanMetricValue>{fmt(llExposure.settledExposure)}</LoanMetricValue>
                          </LoanMetric>
                          <LoanMetric>
                            <LoanMetricLabel>Pending Buy</LoanMetricLabel>
                            <LoanMetricValue style={{ color: '#60a5fa' }}>{fmt(llExposure.pendingBuy)}</LoanMetricValue>
                          </LoanMetric>
                          <LoanMetric>
                            <LoanMetricLabel>Pending Sell</LoanMetricLabel>
                            <LoanMetricValue style={{ color: '#f59e0b' }}>{fmt(llExposure.pendingSell)}</LoanMetricValue>
                          </LoanMetric>
                        </ExposureGrid>
                      </>
                    )}
                  </FacilityCard>
                ) : (
                  <PlaceholderPanel>
                    <PlaceholderInner>
                      {loanCtxLoading ? <Activity size={17} /> : <Wallet size={17} />}
                      <span>{loanCtxLoading ? 'Loading facility context' : 'Facility context pending'}</span>
                    </PlaceholderInner>
                  </PlaceholderPanel>
                )}
              </InstrumentGrid>

              {pickedSecurity && !loanCtx && !loanCtxLoading && (
                <InfoNote $error>No existing loan record found. Use Reference Data to define this facility first.</InfoNote>
              )}
            </WorkspaceBand>

            {persistedTicket && loadedTradeAudit && (
              <>
              {lateBookingActive && (
                <WorkspaceBand>
                  <BandHeader>
                    <BandTitleGroup>
                      <BandEyebrow><ShieldCheck size={14} /> Late-booking control</BandEyebrow>
                      <BandTitle>Post-trade Compliance + Operations approval</BandTitle>
                      <BandSubline>
                        Externally executed confirmation · evidence {loadedTradeAudit.externalEvidenceId || loadedTradeAudit.sourceRef || '--'}
                      </BandSubline>
                    </BandTitleGroup>
                    <ScopePills>
                      <ScopePill $tone={lateBookingFullyApproved ? 'draw' : 'sell'}>
                        {lateBookingFullyApproved ? 'APPROVED TO RELEASE' : 'APPROVALS REQUIRED'}
                      </ScopePill>
                    </ScopePills>
                  </BandHeader>
                  <LateBookingControls>
                    <LateBookingControlCard $approved={!!loadedTradeAudit.lateBookingComplianceApprovedAt}>
                      <LateBookingControlTitle>Compliance approval</LateBookingControlTitle>
                      <BandSubline>
                        {loadedTradeAudit.lateBookingComplianceApprovedAt
                          ? `${loadedTradeAudit.lateBookingComplianceApprovedBy || 'Approved'} · ${loadedTradeAudit.lateBookingComplianceApprovedAt}`
                          : 'Pending authenticated post-trade review'}
                      </BandSubline>
                      {!loadedTradeAudit.lateBookingComplianceApprovedAt && (
                        <LateBookingApprovalButton
                          type="button"
                          disabled={!canApproveLateBookingCompliance || lateBookingApprovalBusy !== null}
                          onClick={() => approveLateBooking(editingTransactionId!, 'compliance')}
                        >
                          {lateBookingApprovalBusy === 'compliance' ? 'Recording...' : 'Record Compliance Approval'}
                        </LateBookingApprovalButton>
                      )}
                    </LateBookingControlCard>
                    <LateBookingControlCard $approved={!!loadedTradeAudit.lateBookingOpsApprovedAt}>
                      <LateBookingControlTitle>Operations approval</LateBookingControlTitle>
                      <BandSubline>
                        {loadedTradeAudit.lateBookingOpsApprovedAt
                          ? `${loadedTradeAudit.lateBookingOpsApprovedBy || 'Approved'} · ${loadedTradeAudit.lateBookingOpsApprovedAt}`
                          : 'Pending authenticated settlement and evidence review'}
                      </BandSubline>
                      {!loadedTradeAudit.lateBookingOpsApprovedAt && (
                        <LateBookingApprovalButton
                          type="button"
                          disabled={!canApproveLateBookingOperations || lateBookingApprovalBusy !== null}
                          onClick={() => approveLateBooking(editingTransactionId!, 'operations')}
                        >
                          {lateBookingApprovalBusy === 'operations' ? 'Recording...' : 'Record Operations Approval'}
                        </LateBookingApprovalButton>
                      )}
                    </LateBookingControlCard>
                  </LateBookingControls>
                  {lateBookingApprovalError && <InfoNote $error>{lateBookingApprovalError}</InfoNote>}
                  <InfoNote>
                    No retroactive preclear. Release remains locked until both approvals are persisted; Execute and Post follow the normal trade lifecycle.
                  </InfoNote>
                </WorkspaceBand>
              )}
              <WorkspaceBand>
                <BandHeader>
                  <BandTitleGroup>
                    <BandEyebrow><FileText size={14} /> Posting lineage</BandEyebrow>
                    <BandTitle>Trade → Posting → IBOR</BandTitle>
                    <BandSubline>
                      {loadedTradeAudit.ticketType || '--'} / {loadedTradeAudit.eventType || '--'}
                      {' · '}{loadedTradeAudit.sourceSystem || '--'} / {loadedTradeAudit.sourceRef || '--'}
                    </BandSubline>
                  </BandTitleGroup>
                  <ScopePills>
                    <ScopePill $tone={(transactionStatus || loadedTradeAudit.status) === 'POSTED' ? 'draw' : 'neutral'}>
                      {(transactionStatus || loadedTradeAudit.status || '--').replace(/_/g, ' ')}
                    </ScopePill>
                  </ScopePills>
                </BandHeader>
                <ExposureGrid>
                  <LoanMetric>
                    <LoanMetricLabel>{isPc ? 'Funded Par' : 'Par / Quantity'}</LoanMetricLabel>
                    <LoanMetricValue>{fmt(loadedTradeAudit.quantity)}</LoanMetricValue>
                  </LoanMetric>
                  {isPc && (
                    <LoanMetric>
                      <LoanMetricLabel>Commitment Exposure</LoanMetricLabel>
                      <LoanMetricValue>
                        {fmt(num(loadedTradeAudit.quantity) + num(loadedTradeAudit.unfundedCommitment))}
                      </LoanMetricValue>
                    </LoanMetric>
                  )}
                  <LoanMetric>
                    <LoanMetricLabel>{isPc ? 'Settlement Principal' : 'Principal'}</LoanMetricLabel>
                    <LoanMetricValue>{fmt(loadedTradeAudit.principal)}</LoanMetricValue>
                  </LoanMetric>
                  <LoanMetric>
                    <LoanMetricLabel>{isPc ? 'Settlement Cash' : 'Net Cash (clean + accrued)'}</LoanMetricLabel>
                    <LoanMetricValue>{fmt(loadedTradeAudit.netAmount)}</LoanMetricValue>
                  </LoanMetric>
                  {!isPc && (
                    <LoanMetric>
                      <LoanMetricLabel>Settlement Cash (with fees)</LoanMetricLabel>
                      <LoanMetricValue>
                        {fmt(num(loadedTradeAudit.netAmount)
                          + (String(loadedTradeAudit.assignmentFeePayer || '').toUpperCase() === 'BUYER' ? num(loadedTradeAudit.assignmentFee) : 0)
                          + num(loadedTradeAudit.otherSettlementFees))}
                      </LoanMetricValue>
                    </LoanMetric>
                  )}
                  <LoanMetric>
                    <LoanMetricLabel>Operator</LoanMetricLabel>
                    <LoanMetricValue>{loadedTradeAudit.executedBy || loadedTradeAudit.createdBy || '--'}</LoanMetricValue>
                  </LoanMetric>
                </ExposureGrid>
                <BandSubline style={{ marginTop: 10 }}>
                  Created {loadedTradeAudit.createdAt || '--'} · Executed {loadedTradeAudit.executedAt || '--'}
                  {' · '}Posted {loadedTradeAudit.postedAt || loadedTradeAudit.updatedAt || '--'}
                </BandSubline>
                {(postingLineage?.trade?.notes || loadedTradeAudit.notes) && (
                  <InfoNote>
                    Balance audit: {postingLineage?.trade?.notes || loadedTradeAudit.notes}
                  </InfoNote>
                )}
                {postingLineage?.postingAudit?.length > 0 && (
                  <InfoNote>
                    Posting correlation {postingLineage.postingAudit[0].correlationId || '--'}
                    {' · '}outcome {postingLineage.postingAudit[postingLineage.postingAudit.length - 1]?.outcome || '--'}
                    {' · '}loan sync {postingLineage.postingAudit[postingLineage.postingAudit.length - 1]?.syncLoanOutcome || '--'}
                  </InfoNote>
                )}
                {postingLineage?.position && (
                  <InfoNote>
                    IBOR position #{postingLineage.position.positionId}
                    {' · '}par {fmt(postingLineage.position.parAmount)}
                    {' · '}cost {fmt(postingLineage.position.costBasis)}
                    {' · '}MV {fmt(postingLineage.position.marketValue)}
                    {' · '}as of {postingLineage.position.asOfDate || '--'}
                  </InfoNote>
                )}
                {postingLineage?.cashMovements?.map((movement: any) => (
                  <InfoNote key={movement.cashMovementId}>
                    Cash #{movement.cashMovementId}: {fmt(movement.amount)} {movement.currency || ''}
                    {' · '}{movement.movementType || '--'} / {movement.status || '--'}
                  </InfoNote>
                ))}
                {postingLineage?.lifecycleEvent && (
                  <InfoNote>
                    Lifecycle #{postingLineage.lifecycleEvent.lifecycleEventId}
                    {' · '}{postingLineage.lifecycleEvent.message || postingLineage.lifecycleEvent.eventType}
                    {' · '}applied position #{postingLineage.lifecycleEvent.appliedPositionId || '--'}
                  </InfoNote>
                )}
                {complianceNoteSummary ? (
                  <ComplianceNoteCard>
                    <ComplianceNoteHeader>
                      <div>
                        <ComplianceNoteTitle>
                          Compliance {complianceNoteSummary.label}
                        </ComplianceNoteTitle>
                        <ComplianceNoteMeta>
                          {complianceNoteSummary.status && (
                            <ComplianceNotePill $tone="warn">
                              Status {complianceNoteSummary.status}
                            </ComplianceNotePill>
                          )}
                          {complianceNoteSummary.mode && (
                            <ComplianceNotePill $tone="neutral">
                              Mode {complianceNoteSummary.mode}
                            </ComplianceNotePill>
                          )}
                          {complianceNoteSummary.resultStatus && (
                            <ComplianceNotePill $tone="neutral">
                              Result {complianceNoteSummary.resultStatus}
                            </ComplianceNotePill>
                          )}
                          <ComplianceNotePill $tone={complianceNoteSummary.messages.length ? 'danger' : 'success'}>
                            {complianceNoteSummary.messages.length ? `${complianceNoteSummary.messages.length} block${complianceNoteSummary.messages.length === 1 ? '' : 's'}` : 'No blocking messages'}
                          </ComplianceNotePill>
                        </ComplianceNoteMeta>
                      </div>
                    </ComplianceNoteHeader>
                    {complianceNoteSummary.messages.length > 0 && (
                      <ComplianceNoteList>
                        {complianceNoteSummary.messages.slice(0, 3).map((message, index) => (
                          <li key={`${index}-${message}`}>{message}</li>
                        ))}
                      </ComplianceNoteList>
                    )}
                    <ComplianceNoteDetails>
                      <summary>Raw audit payload</summary>
                      <ComplianceNoteRaw>{complianceNoteSummary.raw}</ComplianceNoteRaw>
                    </ComplianceNoteDetails>
                  </ComplianceNoteCard>
                ) : (postingLineage?.compliance?.notes || loadedTradeAudit.complianceNotes) ? (
                  <InfoNote style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                    Compliance {postingLineage?.compliance?.decision || '--'}:
                    {' '}{postingLineage?.compliance?.notes || loadedTradeAudit.complianceNotes}
                  </InfoNote>
                ) : null}
              </WorkspaceBand>
              </>
            )}

            <WorkspaceBand>
              <BandHeader>
                <BandTitleGroup>
                  <BandEyebrow>
                    <ModeIcon size={14} />
                    {isPc ? 'Lifecycle event' : 'Trade event'}
                  </BandEyebrow>
                  <BandTitle>{modeMeta.label}</BandTitle>
                  <BandSubline>{modeDisplayEvent} / {modeMeta.side} / {formData.settlementConvention}</BandSubline>
                </BandTitleGroup>
                <ScopePills>
                  <ScopePill $tone="neutral"><CheckCircle2 size={13} /> IBOR trade</ScopePill>
                  <ScopePill $tone={modeMeta.tone}>{modeDisplayEvent}</ScopePill>
                </ScopePills>
              </BandHeader>

              {isPc ? (
                <ModeBar>
                  {(['acquire', 'drawdown', 'paydown'] as PcMode[]).map(m => {
                    const { label, eventType, displayEventType, side, tone, Icon } = pcModeMeta[m];
                    return (
                      <ModeTab
                        key={m}
                        type="button"
                        $active={pcMode === m}
                        $tone={tone}
                        disabled={locked || !pickedSecurity}
                        onClick={() => {
                          setPcMode(m);
                          setFormData((prev: any) => ({ ...prev, transactionType: m === 'paydown' ? 'SELL' : 'BUY', amount: '' }));
                        }}
                      >
                        <ModeGlyph $tone={tone}>
                          <Icon size={17} />
                        </ModeGlyph>
                        <ModeText>
                          <ModeMain>{label}</ModeMain>
                          <ModeMeta>
                            <span>{displayEventType || eventType}</span>
                            <span>{side}</span>
                          </ModeMeta>
                        </ModeText>
                      </ModeTab>
                    );
                  })}
                </ModeBar>
              ) : (
                <>
                  <ModeGroupLabel>PM / Trader — acquisitions &amp; transfers</ModeGroupLabel>
                  <ModeBar>{llTransferTiles.map(renderLlTile)}</ModeBar>
                  <ModeGroupLabel>Operations — use Loan 360 servicing</ModeGroupLabel>
                  <ModeBar>{llOpsTiles.map(renderLlTile)}</ModeBar>
                  <BandSubline style={{ marginTop: 8 }}>
                    Drawdown, paydown, rate reset, interest and fee notices, amendments, and covenant
                    tests use the same governed Loan 360 / Loan Book actions for LL and private credit.
                  </BandSubline>
                </>
              )}

              <PortfolioAssignmentPanel
                $tone={needsManualPortfolio ? modeMeta.tone : 'draw'}
                $error={portfolioResolutionError || (!!errors.portfolio && !llAllocationPanelActive) || !!errors.llAllocations}
              >
                <PortfolioAssignmentHeader>
                  <PortfolioAssignmentTitle>
                    <Landmark size={15} />
                    Portfolio assignment
                  </PortfolioAssignmentTitle>
                  <PortfolioAssignmentMeta>
                    {needsManualPortfolio
                      ? (isPc ? 'Manual selection' : 'Explicit allocation — never inherited from the facility')
                      : 'Committed facility'}
                  </PortfolioAssignmentMeta>
                  {/* AXP-133 (LL-FR-14/D15): opt-in multi-fund block allocation
                      on the Primary Allocation / Secondary Assignment tiles. */}
                  {!isPc && (llMode === 'primary' || llMode === 'secondary') && (
                    <CheckboxRow style={{ marginTop: 10 }}>
                      <input
                        type="checkbox"
                        checked={llUseAllocations}
                        onChange={(e) => setBlockMode(e.target.checked, e.target.checked && llAllocations.length === 0)}
                        disabled={persistedTicket}
                      />
                      Allocate across multiple funds (block)
                    </CheckboxRow>
                  )}
                </PortfolioAssignmentHeader>

                {needsManualPortfolio && llAllocationPanelActive ? (
                  (() => {
                    const blockParNum = parseAmountShorthand(formData.amount);
                    const allocSummary = summarizeLlAllocations(llAllocations, blockParNum);
                    const allocState: 'ok' | 'pending' | 'error' =
                      allocSummary.error ? 'error'
                        : allocSummary.active.length > 0 && blockParNum > 0 ? 'ok'
                        : 'pending';
                    return (
                      <AllocationGrid>
                        <InfoNote style={{ margin: 0 }}>
                          Enter each fund and its par in the Allocation Grid below. This panel reconciles the rows to block par.
                        </InfoNote>
                        <AllocationTotals $state={allocState}>
                          <span>
                            Allocated {fmtExact(allocSummary.total)}
                            {' of '}
                            {blockParNum > 0 ? fmtExact(blockParNum) : 'block par'}
                          </span>
                          <span>
                            {allocState === 'ok' ? 'Fully allocated'
                              : allocState === 'error' ? 'Does not reconcile'
                              : 'Awaiting rows'}
                          </span>
                        </AllocationTotals>
                        {errors.llAllocations && <FieldError>{errors.llAllocations}</FieldError>}
                        <InfoNote style={{ margin: '2px 0 0' }}>
                          Books one child trade per fund with pro-rata accrued and assignment fee
                          (largest-remainder pennies). The block record itself never creates a
                          position or cash (LL-FR-14).
                        </InfoNote>
                      </AllocationGrid>
                    );
                  })()
                ) : needsManualPortfolio ? (
                  <FormGroup style={{ margin: 0 }}>
                    <Label htmlFor="portfolio">Portfolio*</Label>
                    <Select
                      id="portfolio"
                      value={formData.portfolio}
                      onChange={(e) => handlePortfolioChange(e.target.value)}
                      className={errors.portfolio && !llAllocationPanelActive ? 'error' : ''}
                      disabled={isFieldDisabled('portfolio')}
                    >
                      <option value="">Select Portfolio</option>
                      {(dimensionData.portfolios || []).map((p: any) => (
                        <option key={p.id} value={p.portfolioName}>
                          {p.portfolioName}
                        </option>
                      ))}
                    </Select>
                    {errors.portfolio && !llAllocationPanelActive && <FieldError>{errors.portfolio}</FieldError>}
                  </FormGroup>
                ) : (
                  <PortfolioAssignmentValue $error={portfolioResolutionError}>
                    <span>{portfolioResolutionValue}</span>
                    <PortfolioSourceBadge $error={portfolioResolutionError}>
                      {portfolioResolutionError ? 'Blocked' : loanCtx?.portfolioName ? 'Committed' : 'Resolving'}
                    </PortfolioSourceBadge>
                  </PortfolioAssignmentValue>
                )}
              </PortfolioAssignmentPanel>
            </WorkspaceBand>

            {pickedSecurity && (
              <WorkspaceBand>
                <BandHeader>
                  <BandTitleGroup>
                    <BandEyebrow>
                      <Banknote size={14} />
                      Ticket economics
                    </BandEyebrow>
                    <BandTitle>{economicsBandTitle}</BandTitle>
                    <BandSubline>{instrumentCode}</BandSubline>
                  </BandTitleGroup>
                  <ScopePills>
                    {!isPc && modeMeta.channel && (
                      <ScopePill $tone={modeMeta.tone}>Channel: {modeMeta.channel}</ScopePill>
                    )}
                    <ScopePill $tone="neutral"><Percent size={13} /> {formData.pricingSource}</ScopePill>
                    <ScopePill $tone="neutral">{formData.currency}</ScopePill>
                  </ScopePills>
                </BandHeader>

                <DetailGrid>
                  <DetailPanel>
                    <DetailPanelHeader>
                      <DetailPanelTitle>
                        <CircleDollarSign size={16} />
                        Economics
                      </DetailPanelTitle>
                      <SettlementTag>{modeDisplayEvent}</SettlementTag>
                    </DetailPanelHeader>
                    {loanCtx ? (
                      <>
                        {/* LL two-dimension classification (LL-FR-3, D4): channel is
                            fixed by the tile; transfer form is the second explicit
                            dimension. PARTICIPATION is Phase 2 → option disabled. */}
                        {!isPc && isTransferMode && llMode !== 'primary' && (
                          <ChannelRow>
                            <FormGroup style={{ margin: 0, minWidth: 220 }}>
                              <Label>Transfer Form*</Label>
                              <Select
                                value={formData.llTransferForm}
                                onChange={(e) => handleInputChange('llTransferForm', e.target.value)}
                                disabled={locked || llMode === 'lsta-sell'}
                              >
                                <option value="ASSIGNMENT">LSTA Assignment</option>
                                {llMode === 'secondary' && <option value="PARTICIPATION">Participation (LSTA)</option>}
                              </Select>
                            </FormGroup>
                            {formData.llTransferForm === 'ASSIGNMENT' && (
                              <CheckboxRow>
                                <input
                                  type="checkbox"
                                  checked={!!formData.llAssignmentOnly}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    setFormData((prev: any) => ({ ...prev, llAssignmentOnly: checked }));
                                  }}
                                  disabled={locked}
                                />
                                Assignment-only election (no participation fallback)
                              </CheckboxRow>
                            )}
                          </ChannelRow>
                        )}

                        {/* Secondary Participation (LL-FR-3.4): a participation is NOT an
                            assignment — the seller (grantor) stays lender of record; the
                            participant holds a synthetic position with the grantor as
                            counterparty. Optional grantor-retained servicing fee + consent. */}
                        {!isPc && llMode === 'secondary' && formData.llTransferForm === 'PARTICIPATION' && (
                          <FormRow>
                            <FormGroup>
                              <Label>Participation Fee (bps, grantor-retained)</Label>
                              <Input
                                name="llParticipationFeeBps"
                                value={formData.llParticipationFeeBps || ''}
                                onChange={(e) => handleInputChange('llParticipationFeeBps', e.target.value)}
                                onWheel={blurOnWheel}
                                disabled={locked}
                                placeholder="e.g. 25 (default 0)"
                              />
                            </FormGroup>
                            <FormGroup>
                              <Label>Grantor (intra-book sub-participation, optional)</Label>
                              <Select
                                name="llGrantorPortfolioId"
                                value={formData.llGrantorPortfolioId || ''}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (!val) {
                                    // External counterparty: no internal grantor portfolio —
                                    // the seller/assignor stays a free-text the user fills.
                                    setFormData((prev: any) => ({ ...prev, llGrantorPortfolioId: '' }));
                                    return;
                                  }
                                  // Internal portfolio grantor: carry the id (drives the
                                  // PARTICIPATION_GRANTED contra) and auto-fill the seller /
                                  // assignor of record with the portfolio's name (still editable).
                                  const p = (dimensionData.portfolios || []).find(
                                    (x: any) => String(x.id) === String(val));
                                  setFormData((prev: any) => ({
                                    ...prev,
                                    llGrantorPortfolioId: val,
                                    llSeller: p ? p.portfolioName : prev.llSeller,
                                  }));
                                }}
                                disabled={locked}
                              >
                                <option value="">External counterparty (enter name below)</option>
                                {(dimensionData.portfolios || []).map((p: any) => (
                                  <option key={p.id} value={p.id}>
                                    {p.portfolioName}
                                  </option>
                                ))}
                              </Select>
                            </FormGroup>
                            <CheckboxRow>
                              <input
                                type="checkbox"
                                checked={formData.llParticipationConsent !== false}
                                onChange={(e) => setFormData((prev: any) => ({ ...prev, llParticipationConsent: e.target.checked }))}
                                disabled={locked}
                              />
                              Consent rights on sacred/money terms (principal, rate, maturity, collateral release)
                            </CheckboxRow>
                          </FormRow>
                        )}

                        <FormRow>
                          <FormGroup style={{ gridColumn: isTransferMode ? 'span 2' : '1 / -1' }}>
                            <Label>{amountLabel}</Label>
                            <Input
                              name="amount"
                              value={formData.amount}
                              onChange={(e) => handleInputChange('amount', e.target.value)}
                              onWheel={blurOnWheel}
                              onBlur={(e) => {
                                const parsed = parseAmountShorthand(e.target.value);
                                if (parsed > 0) {
                                  setFormData((prev: any) => ({ ...prev, amount: formatAmountWithCommas(parsed) }));
                                }
                                handleInputBlur('amount', parsed.toString());
                              }}
                              disabled={locked || (!isPc && llMode === 'drawdown')}
                              placeholder={amountPlaceholder}
                            />
                            {errors.amount && <FieldError>{errors.amount}</FieldError>}
                          </FormGroup>

                          {isTransferMode && (isPc || llMode !== 'primary') && (
                            <FormGroup>
                              <Label>{isPc ? 'Transfer Price (%)' : 'Clean Price (%)*'}</Label>
                              <Input
                                name="price"
                                value={formData.price}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  handleInputChange('price', value);
                                  if (!isPc && (llMode === 'secondary' || llMode === 'lsta-sell')) {
                                    void applyServerSettlementConvention(formData.tradeDate, value);
                                  }
                                }}
                                onWheel={blurOnWheel}
                                disabled={locked}
                                placeholder="e.g. 98.5"
                              />
                              {errors.price && <FieldError>{errors.price}</FieldError>}
                            </FormGroup>
                          )}
                        </FormRow>

                        {/* ── Primary Allocation (LL-FR-3.2) ── */}
                        {!isPc && llMode === 'primary' && (
                          <>
                            {llIsStrip && (
                              <InfoNote>
                                {loanCtx?.facilityType === 'DELAYED_DRAW' ? 'DDTL' : 'RCF'} commitment strip —
                                enter the total commitment as <strong>Awarded</strong> and the portion funded
                                at close (if any) as <strong>Par Amount</strong>. Undrawn = Awarded − Par.
                                Leave Par Amount blank for a fully-undrawn commitment (no cash, no settle);
                                a funded portion books a real position and settles normally (AXP-146).
                              </InfoNote>
                            )}
                            <FormRow>
                              <FormGroup>
                                <Label>Arranger / Bookrunner*</Label>
                                <Input
                                  value={formData.llArranger}
                                  onChange={(e) => handleInputChange('llArranger', e.target.value)}
                                  disabled={locked}
                                  placeholder="e.g. JP Morgan"
                                />
                                {errors.llArranger && <FieldError>{errors.llArranger}</FieldError>}
                              </FormGroup>
                              <FormGroup>
                                <Label>Allocation Source</Label>
                                <Input
                                  value={formData.llAllocationSource}
                                  onChange={(e) => handleInputChange('llAllocationSource', e.target.value)}
                                  disabled={locked}
                                  placeholder="e.g. DirectBooks ref / arranger email"
                                />
                              </FormGroup>
                            </FormRow>
                            <FormRow>
                              <FormGroup>
                                <Label>Requested (USD)*</Label>
                                <Input
                                  value={formData.llRequested}
                                  onChange={(e) => handleInputChange('llRequested', e.target.value)}
                                  onWheel={blurOnWheel}
                                  onBlur={(e) => {
                                    const parsed = parseAmountShorthand(e.target.value);
                                    if (parsed > 0) handleInputChange('llRequested', formatAmountWithCommas(parsed));
                                  }}
                                  disabled={locked}
                                  placeholder="e.g. 25,000,000"
                                />
                                {errors.llRequested && <FieldError>{errors.llRequested}</FieldError>}
                              </FormGroup>
                              <FormGroup>
                                <Label>Circled (USD)*</Label>
                                <Input
                                  value={formData.llCircled}
                                  onChange={(e) => handleInputChange('llCircled', e.target.value)}
                                  onWheel={blurOnWheel}
                                  onBlur={(e) => {
                                    const parsed = parseAmountShorthand(e.target.value);
                                    if (parsed > 0) handleInputChange('llCircled', formatAmountWithCommas(parsed));
                                  }}
                                  disabled={locked}
                                  placeholder="e.g. 15,000,000"
                                />
                                {errors.llCircled && <FieldError>{errors.llCircled}</FieldError>}
                              </FormGroup>
                            </FormRow>
                            <FormRow>
                              <FormGroup>
                                <Label>Awarded (USD)*</Label>
                                <Input
                                  value={formData.llAwarded}
                                  onChange={(e) => handleInputChange('llAwarded', e.target.value)}
                                  onWheel={blurOnWheel}
                                  onBlur={(e) => {
                                    const parsed = parseAmountShorthand(e.target.value);
                                    if (parsed > 0) handleInputChange('llAwarded', formatAmountWithCommas(parsed));
                                  }}
                                  disabled={locked}
                                  placeholder="e.g. 10,000,000"
                                />
                                {errors.llAwarded && <FieldError>{errors.llAwarded}</FieldError>}
                              </FormGroup>
                              <FormGroup>
                                <Label>Issue Price (%)*</Label>
                                <Input
                                  value={formData.llIssuePrice}
                                  onChange={(e) => handleInputChange('llIssuePrice', e.target.value)}
                                  onWheel={blurOnWheel}
                                  disabled={locked}
                                  placeholder="e.g. 99.0"
                                />
                                {errors.llIssuePrice && <FieldError>{errors.llIssuePrice}</FieldError>}
                              </FormGroup>
                            </FormRow>
                            <FormRow>
                              <FormGroup>
                                <Label>Expected Close Date*</Label>
                                <DateField
                                  value={formData.llExpectedClose ?? ''}
                                  disabled={locked}
                                  onChange={iso => {
                                  const v = iso;
                                  setFormData((prev: any) => ({ ...prev, llExpectedClose: v, settleDate: v || prev.settleDate }));
                                  }}
                                />
                                {errors.llExpectedClose && <FieldError>{errors.llExpectedClose}</FieldError>}
                              </FormGroup>
                              <FormGroup>
                                <Label>Evidence Ref (allocation)</Label>
                                <Input
                                  value={formData.llEvidenceId}
                                  onChange={(e) => handleInputChange('llEvidenceId', e.target.value)}
                                  disabled={locked}
                                  placeholder="e.g. ALLOC-2026-0117"
                                />
                              </FormGroup>
                            </FormRow>
                          </>
                        )}

                        {/* ── Secondary Assignment buy (LL-FR-3.3) ── */}
                        {!isPc && llMode === 'secondary' && (
                          <>
                            <FormRow>
                              <FormGroup>
                                <Label>Seller / Assignor of Record*</Label>
                                <Input
                                  value={formData.llSeller}
                                  onChange={(e) => handleInputChange('llSeller', e.target.value)}
                                  disabled={locked}
                                  placeholder="Legal seller on the LSTA confirm"
                                />
                                {errors.llSeller && <FieldError>{errors.llSeller}</FieldError>}
                              </FormGroup>
                              <FormGroup>
                                <Label>Purchased Accrued (USD)</Label>
                                <Input
                                  value={formData.llPurchasedAccrued}
                                  onChange={(e) => handleInputChange('llPurchasedAccrued', e.target.value)}
                                  onWheel={blurOnWheel}
                                  onBlur={(e) => {
                                    const parsed = parseAmountShorthand(e.target.value);
                                    if (parsed > 0) handleInputChange('llPurchasedAccrued', formatAmountWithCommas(parsed));
                                  }}
                                  disabled={locked}
                                  placeholder="e.g. 41,250"
                                />
                              </FormGroup>
                            </FormRow>
                            <FormRow>
                              <FormGroup>
                                <Label>Assignment Fee (USD)</Label>
                                <Input
                                  value={formData.llAssignmentFee}
                                  onChange={(e) => handleInputChange('llAssignmentFee', e.target.value)}
                                  onWheel={blurOnWheel}
                                  disabled={locked}
                                  placeholder="e.g. 3,500"
                                />
                              </FormGroup>
                              <FormGroup>
                                <Label>Fee Payer</Label>
                                <Select
                                  value={formData.llFeePayer}
                                  onChange={(e) => handleInputChange('llFeePayer', e.target.value)}
                                  disabled={locked}
                                >
                                  <option value="BUYER">Buyer</option>
                                  <option value="SELLER">Seller</option>
                                  <option value="SPLIT">Split</option>
                                  <option value="WAIVED">Waived</option>
                                </Select>
                              </FormGroup>
                            </FormRow>
                            {llIsStrip && (
                              <FormRow>
                                <FormGroup>
                                  <Label>Undrawn Commitment (USD)</Label>
                                  <Input
                                    value={formData.llUnfundedCommitment}
                                    onChange={(e) => handleInputChange('llUnfundedCommitment', e.target.value)}
                                    onWheel={blurOnWheel}
                                    onBlur={(e) => {
                                      const parsed = parseAmountShorthand(e.target.value);
                                      if (parsed > 0) handleInputChange('llUnfundedCommitment', formatAmountWithCommas(parsed));
                                    }}
                                    disabled={locked}
                                    placeholder="e.g. 6,000,000"
                                  />
                                  <InfoNote>
                                    {loanCtx?.facilityType === 'DELAYED_DRAW' ? 'DDTL' : 'RCF'} strip: the
                                    funded par above is the drawn amount; this is the undrawn commitment
                                    acquired with it. Total commitment = funded + undrawn.
                                  </InfoNote>
                                </FormGroup>
                              </FormRow>
                            )}
                            <FormRow>
                              <FormGroup>
                                <Label>Evidence Ref (LSTA confirm)</Label>
                                <Input
                                  value={formData.llEvidenceId}
                                  onChange={(e) => handleInputChange('llEvidenceId', e.target.value)}
                                  disabled={locked}
                                  placeholder="e.g. LSTA-CONF-88213 / ClearPar id"
                                />
                              </FormGroup>
                            </FormRow>
                          </>
                        )}

                        {/* ── LSTA Sell ── */}
                        {!isPc && llMode === 'lsta-sell' && (
                          <>
                            <FormRow>
                              <FormGroup>
                                <Label>Buyer / Assignee*</Label>
                                <Input
                                  value={formData.llBuyer}
                                  onChange={(e) => handleInputChange('llBuyer', e.target.value)}
                                  disabled={locked}
                                  placeholder="Legal buyer on the LSTA confirm"
                                />
                                {errors.llBuyer && <FieldError>{errors.llBuyer}</FieldError>}
                              </FormGroup>
                              <FormGroup>
                                <Label>Assignment Fee (USD)</Label>
                                <Input
                                  value={formData.llAssignmentFee}
                                  onChange={(e) => handleInputChange('llAssignmentFee', e.target.value)}
                                  onWheel={blurOnWheel}
                                  disabled={locked}
                                  placeholder="e.g. 3,500"
                                />
                              </FormGroup>
                            </FormRow>
                            <FormRow>
                              <FormGroup>
                                <Label>Fee Payer</Label>
                                <Select
                                  value={formData.llFeePayer}
                                  onChange={(e) => handleInputChange('llFeePayer', e.target.value)}
                                  disabled={locked}
                                >
                                  <option value="BUYER">Buyer</option>
                                  <option value="SELLER">Seller</option>
                                  <option value="SPLIT">Split</option>
                                  <option value="WAIVED">Waived</option>
                                </Select>
                              </FormGroup>
                              <FormGroup>
                                <Label>Evidence Ref (LSTA confirm)</Label>
                                <Input
                                  value={formData.llEvidenceId}
                                  onChange={(e) => handleInputChange('llEvidenceId', e.target.value)}
                                  disabled={locked}
                                  placeholder="e.g. LSTA-CONF-88213"
                                />
                              </FormGroup>
                            </FormRow>
                          </>
                        )}
                      </>
                    ) : (
                      <PlaceholderPanel style={{ minHeight: 118 }}>
                        <PlaceholderInner>
                          <Wallet size={17} />
                          <span>Economics pending facility context</span>
                        </PlaceholderInner>
                      </PlaceholderPanel>
                    )}
                  </DetailPanel>

                  <DetailPanel>
                    <DetailPanelHeader>
                      <DetailPanelTitle>
                        <Activity size={16} />
                        Desk details
                      </DetailPanelTitle>
                      <SettlementTag>
                        {!isPc && llMode === 'primary'
                          ? 'Primary close'
                          : !isPc && (llMode === 'secondary' || llMode === 'lsta-sell')
                            ? (formData.settlementConvention || (llSettleRefused ? 'Settlement refused' : 'Resolving settlement'))
                            : formData.settlementConvention}
                      </SettlementTag>
                    </DetailPanelHeader>
                    <FormRow>
                      <FormGroup>
                        <Label>Trade Date*</Label>
                        <DateField
                          value={formData.tradeDate ?? ''}
                          onProblem={(problem, fix) => setTradeDateProblem(problem ? { problem, fix } : null)}
                          disabled={locked}
                          calendars={calendarsFor(isPc ? 'PRIVATE_CREDIT' : 'LEVERAGED_LOAN', formData.currency)}
                          onChange={iso => {
                            if (isPc) {
                              handleInputChange('tradeDate', iso);
                              syncPcSettleDate(setFormData, iso, formData.currency);
                            } else {
                              handleLlTradeDateChange(iso);
                            }
                          }}
                        />
                        {errors.tradeDate && <FieldError>{errors.tradeDate}</FieldError>}
                      </FormGroup>
                      <FormGroup>
                        <Label>
                          {!isPc && llMode === 'primary' ? 'Expected Close*' : 'Settle Date'}
                        </Label>
                        <DateField
                          value={formData.settleDate ?? ''}
                          disabled={locked || (!isPc && (llMode === 'secondary' || llMode === 'lsta-sell'))}
                          calendars={settlementCalendars({ ticketType: isPc ? 'PRIVATE_CREDIT' : 'LEVERAGED_LOAN', currency: formData.currency })}
                          onChange={iso => handleInputChange('settleDate', iso)}
                        />
                        {llSettleRefused && <FieldError>{llSettleError}</FieldError>}
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                      <FormGroup>
                        <Label>Asset Manager</Label>
                        <Select
                          value={formData.assetManager}
                          onChange={(e) => handleInputChange('assetManager', e.target.value)}
                          disabled={locked}
                        >
                          <option value="">Select</option>
                          {formData.assetManager
                            && !(dimensionData.assetManagers || []).some((am: any) => am.assetManagerName === formData.assetManager) && (
                              <option value={formData.assetManager}>{formData.assetManager}</option>
                            )}
                          {(dimensionData.assetManagers || []).map((am: any) => (
                            <option key={am.id} value={am.assetManagerName}>{am.assetManagerName}</option>
                          ))}
                        </Select>
                      </FormGroup>
                      <FormGroup>
                        <Label>Portfolio Manager</Label>
                        <Select
                          value={formData.portfolioManager}
                          onChange={(e) => handleInputChange('portfolioManager', e.target.value)}
                          disabled={locked}
                        >
                          <option value="">Select</option>
                          {formData.portfolioManager
                            && !(dimensionData.portfolioManagers || []).some((pm: any) => pm.portfolioManagerName === formData.portfolioManager) && (
                              <option value={formData.portfolioManager}>{formData.portfolioManager}</option>
                            )}
                          {(dimensionData.portfolioManagers || []).map((pm: any) => (
                            <option key={pm.id} value={pm.portfolioManagerName}>{pm.portfolioManagerName}</option>
                          ))}
                        </Select>
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                      <FormGroup>
                        <Label>Executed By{transactionStatus === 'SENT' ? '*' : ''}</Label>
                        <Select
                          id="executedBy"
                          value={formData.executedBy}
                          onChange={(e) => handleInputChange('executedBy', e.target.value)}
                          onBlur={(e) => handleInputBlur('executedBy', e.target.value)}
                          disabled={isFieldDisabled('executedBy')}
                          className={errors.executedBy ? 'error' : ''}
                        >
                          <option value="">Select</option>
                          {(dimensionData.users || []).map((user: any) => (
                            <option key={user.id} value={user.fullName}>
                              {user.fullName}{user.role ? ` (${user.role})` : ''}
                            </option>
                          ))}
                        </Select>
                        {errors.executedBy && <FieldError>{errors.executedBy}</FieldError>}
                      </FormGroup>
                      <FormGroup>
                        <Label>Submitted By</Label>
                        <Input
	                          value={formData.submittedBy}
	                          onChange={(e) => handleInputChange('submittedBy', e.target.value)}
	                          disabled={locked}
	                          placeholder={defaultSubmittedBy || 'Current user'}
	                        />
                      </FormGroup>
                    </FormRow>
                  </DetailPanel>
                </DetailGrid>
              </WorkspaceBand>
            )}
          </CreditWorkspace>
        );
      }}
    </BaseTicketScreen>
  );
};

export default PrivateFixedIncomeTicketScreen;
